<template>
  <div class="lsa-modern-dashboard pt-3" :class="me ? '' : 'p-5'">

    <!-- ================================================================= -->
    <!-- 1. TOP BAR & DATE / LSA CONTROLS                                  -->
    <!-- ================================================================= -->
    <div v-if="!loading" class="pagehead flex items-center justify-between flex-wrap gap-4 mb-2">
      <div class="flex items-center gap-3 flex-wrap">
        <h1 class="text-2xl font-extrabold text-[#16283c]">
          {{ businessName || company_name || 'Dashboard' }}
        </h1>
        <span v-if="me">
          <span v-if="(me.lsa_cid || me.integrations.google_ads.status == 1)" class="chip ctx">Google LSA: Connected ✓</span>
          <span v-else-if="(me.lsa_cid || me.integrations.google_ads.status != 1)" class="chip ctx" style="background: #fefce8; color: #854d0e;">Google LSA: Pending Approval</span>
          <span v-else class="chip ctx" style="background: #b91c1c; color: #FFFFFF;">Google LSA: Not Submitted!</span>
        </span>
      </div>

      <!-- CUSTOM DATE RANGE FILTER (At Top of Page) -->
      <div class="relative ml-auto">
        <div class="daterange-picker-btn cursor-pointer flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 shadow-sm hover:border-[#2fa7b8] transition-colors" @click="show_picker = !show_picker" style="height: 40px;">
          <i class="mdi mdi-calendar-blank-outline text-base text-[#2fa7b8]"></i>
          <span class="text-xs font-semibold text-slate-700 whitespace-nowrap">{{ formatDate(date_filter[0], 'MMM DD, YYYY') }} - {{ date_filter[1] ? formatDate(date_filter[1], 'MMM DD, YYYY') : '' }}</span>
        </div>

        <!-- Date Picker Popup -->
        <div v-if="show_picker" class="absolute right-0 top-12 z-50 bg-white p-4 rounded-2xl shadow-2xl border border-slate-200" style="width: 500px;">
          <div class="flex">
            <div class="pr-3 border-r border-slate-200">
              <div class="mb-2 font-bold text-sm text-slate-800">Filter by Date</div>
              <v-date-picker v-model="date_filter" range no-title scrollable style="border: 0px;">
                <v-spacer></v-spacer>
                <v-btn text color="#2fa7b8" @click="show_picker = false">Cancel</v-btn>
                <v-btn text color="#2fa7b8" @click="filterDate();">UPDATE</v-btn>
              </v-date-picker>
            </div>

            <div class="pl-3 text-xs space-y-1.5 flex-1 max-h-[300px] overflow-y-auto">
              <div v-for="opt in ['This Week', 'Last Week', 'This Month', 'Last Month', 'Last 30 days', 'Last 60 days', 'Last 90 days', 'This Year', 'Last Year', 'Last 6 months', 'Last 12 months', 'Last 24 months', 'Last 36 months', 'All Time']" :key="opt">
                <a
                  href="javascript:void(0);"
                  @click="applyDate(opt)"
                  :class="selected_date_text === opt ? 'font-extrabold text-[#2fa7b8]' : 'text-slate-600 hover:text-slate-900'"
                  class="block py-0.5"
                >{{ opt }}</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- <p class="sub mb-6">
      One screen: how healthy the account is, what the AI already handled for you, and the few things only you can do.
    </p> -->

    <!-- ================================================================= -->
    <!-- 2. HERO ROW: ACCOUNT HEALTH / RECOVERED / TIME SAVED / DO THIS NOW -->
    <!-- ================================================================= -->

    <!-- HERO SKELETON LOADING STATE -->
    <div v-if="loading || loadingHealth" class="grid hero mb-6">
      <div v-for="i in 4" :key="'hero-skel-' + i" class="card p-4">
        <v-skeleton-loader type="article"></v-skeleton-loader>
      </div>
    </div>

    <div v-else class="grid hero mb-6">
      <!-- HERO CARD 1: ACCOUNT HEALTH RING -->
      <div class="card flex flex-col justify-between">
        <div class="flex gap-3.5 items-center">
          <div class="ring" :style="heroRingStyle">
            <div class="inner">
              <b>{{ healthScoreValue !== null ? healthScoreValue : '--' }}<small>/100</small></b>
              <span>{{ healthScoreLabel }}</span>
            </div>
          </div>
          <div style="padding-left: 10px;">
            <div class="k uppercase">ACCOUNT HEALTH</div>
            <div class="why">
              {{ computedHealthSummary }}
            </div>
          </div>
        </div>
        <div class="bands">
          <span><span class="dot" style="background:#1d7a46"></span>90–100</span>
          <span><span class="dot" style="background:#c9a227"></span>80–89</span>
          <span><span class="dot" style="background:#d07c2e"></span>70–79</span>
          <span><span class="dot" style="background:#c0392b"></span>&lt;70</span>
        </div>
      </div>

      <!-- HERO CARD 2: RECOVERED AUTOMATICALLY -->
      <div class="card flex flex-col justify-between">
        <div>
          <div class="k uppercase">RECOVERED FOR YOU — AUTOMATICALLY</div>
          <div class="money mt-1 mb-1">${{ dashboard_data.refunds_amount || '0.00' }} back</div>
          <div class="baseline">
            + ${{ dashboard_data.refunds_amount_total || '0.00' }} pending · {{ dashboard_data.stats ? dashboard_data.stats.approved_disputes : 0 }} disputes filed by the AI — nothing for you to do
          </div>
        </div>
        <div>
          <a class="link cursor-pointer" @click="filterDisputedLeads()">
            See disputed leads →
          </a>
        </div>
      </div>

      <!-- HERO CARD 3: TIME SAVED -->
      <div class="card flex flex-col justify-between relative">
        <div>
          <div class="k uppercase">TIME SAVED</div>
          <div class="v my-1">
            {{ (dashboard_data.impact_stats && dashboard_data.impact_stats.hoursSaved) || hoursSaved || '0' }} <small>hrs</small>
          </div>

          <!-- BASELINE: TOP 3 METRICS & HOVER/CLICK DROPDOWN -->
          <div class="baseline text-xs text-slate-500 leading-snug">
            <!-- Visible Top 3 Summary -->
            <span>
              <template v-for="(metric, idx) in topImpactMetrics">
                <span :key="'metric-' + idx">
                  <strong>{{ metric.value }}</strong> {{ metric.label.toLowerCase() }}<template v-if="idx < topImpactMetrics.length - 1"> · </template>
                </span>
              </template>
            </span>

            <!-- Dropdown Wrapper with Vue Mouse Events -->
            <div 
              class="inline-block relative ml-1"
              @mouseenter="showImpactBreakdown = true"
              @mouseleave="showImpactBreakdown = false"
            >
              <span class="text-indigo-600 font-bold cursor-pointer link cursor-pointer">
                View all breakdown ▾
              </span>

              <!-- HOVER DROPDOWN PANEL -->
              <div 
                v-if="showImpactBreakdown"
                class="absolute left-0 bottom-full mb-2 w-72 bg-white rounded-xl shadow-xl border border-slate-200 p-4 z-50 text-slate-800 text-left"
                style="width: 350px;"
              >
                <div class="font-bold text-xs uppercase text-slate-400 mb-2 border-b border-slate-100 pb-1">
                  Complete Impact Breakdown
                </div>
                
                <div class="space-y-1.5 text-xs">
                  <div 
                    v-for="item in impactMetricsList" 
                    :key="item.label"
                    class="flex justify-between items-center py-0.5 border-b border-slate-50 last:border-0"
                  >
                    <span class="text-slate-600">{{ item.label }}</span>
                    <span class="font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded-md">{{ item.value }}</span>
                  </div>
                </div>

                <div class="mt-3 pt-2 border-t border-slate-100 text-[10px] text-slate-400">
                  Calculated live from your current period dashboard data.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- HERO CARD 4: DO THIS NOW ACTION CARD -->
      <div class="card action flex flex-col justify-between">
        <div>
          <div class="k uppercase">DO THIS NOW</div>
          <div class="font-bold text-xs text-[#16283c] mt-1.5 leading-snug">
            {{ setUpNext.length > 0 ? setUpNext[0].title : 'Review unhandled leads or filter missed inquiries requiring team follow-up.' }}
          </div>
          <div class="text-[11px] text-slate-500 mt-1" v-if="setUpNext.length > 0 && setUpNext[0].description">
            {{ setUpNext[0].description }}
          </div>
        </div>
        <div>
          <router-link v-if="setUpNext.length > 0 && setUpNext[0].route" :to="setUpNext[0].route" class="cta teal cursor-pointer border-0 text-white inline-block">
            {{ setUpNext[0].action_label || 'Set up' }}
          </router-link>
          <button v-else class="cta teal cursor-pointer border-0" @click="filterMissedCalls();">
            Filter Pending Leads
          </button>
        </div>
      </div>
    </div>

    <!-- ================================================================= -->
    <!-- 3. LEADS THIS PERIOD                                             -->
    <!-- ================================================================= -->
    <h2 class="rowlabel">Leads this period</h2>

    <!-- LOADING STATE FOR LEADS THIS PERIOD -->
    <div v-if="loading || loadingMessages" class="grid cards4 mb-6">
      <div v-for="i in 4" :key="'skel-leads-' + i" class="card">
        <v-skeleton-loader type="article"></v-skeleton-loader>
      </div>
    </div>

    <div v-else class="grid cards4 mb-6">
      <!-- TOTAL LEADS -->
      <div class="card">
        <div class="k">Real Leads</div>
        <div class="v">{{ dashboard_data.total_leads || 0 }}</div>
        <span class="badge" :class="getTrendBadgeClass(comparisons.total_leads)">
          {{ comparisons.total_leads > 0 ? '↗' : '↘' }} {{ comparisons.total_leads || 0 }}%
        </span>
        <div class="baseline">vs {{ dashboard_data_prev.total_leads || 0 }} last period</div>
        <div class="converted-total" aria-live="polite">
          <span><i class="mdi mdi-check-circle-outline" aria-hidden="true"></i> Converted leads</span>
          <strong>{{ convertedStatusCount }}</strong>
        </div>
      </div>

      <!-- CALLS ANSWERED <30s -->
      <div class="card">
        <div class="k">Calls Answered &lt;30s</div>
        <div class="v">{{ answeredUnder30sRate }}%</div>
        <span class="badge" :class="getTrendBadgeClass(comparisons.lsa_30_seconds_response_rate)">
          {{ comparisons.lsa_30_seconds_response_rate > 0 ? '↗' : '↘' }} {{ comparisons.lsa_30_seconds_response_rate || 0 }}%
        </span>
        <div class="baseline">vs {{ dashboard_data_prev.stats.lsa_30_seconds_response_rate || 0 }}% last period</div>
      </div>

      <!-- MESSAGE LEADS -->
      <div class="card">
        <div class="k">Message Leads</div>
        <div class="v">{{ dashboard_data.total_message_leads || 0 }}</div>
        <span class="badge" :class="getTrendBadgeClass(comparisons.total_message_leads)">
          {{ comparisons.total_message_leads > 0 ? '↗' : '↘' }} {{ comparisons.total_message_leads || 0 }}%
        </span>
        <div class="baseline">vs {{ dashboard_data_prev.total_message_leads || 0 }} last period</div>
      </div>

      <!-- SPAM BLOCKED -->
      <div class="card">
        <div class="k">Robocalls Blocked</div>
        <div class="v">{{ dashboard_data.stats ? dashboard_data.stats.blocked_robo_calls : 0 }}</div>
        <span class="badge up">$0 charged</span>
        <div class="baseline">Kept off your line and out of your lead count</div>
      </div>
    </div>

    <!-- ================================================================= -->
    <!-- SEARCH IMPRESSIONS & VISIBILITY (LSA)                            -->
    <!-- ================================================================= -->
    <template v-if="isLsaSource">
      <h2 class="rowlabel">Search impressions &amp; visibility</h2>

      <!-- LOADING STATE FOR IMPRESSIONS -->
      <div v-if="loading || loadingMessages" class="grid cards3 mb-6">
        <div v-for="i in 3" :key="'skel-impr-' + i" class="card">
          <v-skeleton-loader type="article"></v-skeleton-loader>
        </div>
      </div>

      <div v-else class="grid cards3 mb-6">
        <!-- 1. AD IMPRESSIONS -->
        <div class="card" title="The number of times your ad appeared in search results during the selected date range.">
          <div class="k">Ad Impressions</div>
          <div class="v">{{ (dashboard_data.stats && dashboard_data.stats.total_impressions ? Number(dashboard_data.stats.total_impressions).toLocaleString() : '0') }}</div>
          <span class="badge" :class="getTrendBadgeClass(comparisons.total_impressions)">
            {{ comparisons.total_impressions > 0 ? '↗' : '↘' }} {{ comparisons.total_impressions || 0 }}%
          </span>
          <div class="baseline">vs {{ (dashboard_data_prev.stats && dashboard_data_prev.stats.total_impressions ? Number(dashboard_data_prev.stats.total_impressions).toLocaleString() : '0') }} last period</div>
        </div>

        <!-- 2. TOP IMPRESSION RATE -->
        <div class="card" title="During the selected date range, the percentage of your impressions that are shown anywhere above results that aren't paid for.">
          <div class="k">Top Impression Rate on Search</div>
          <div class="v">{{ (dashboard_data.stats && dashboard_data.stats.top_impression_rate) || '0.00' }}%</div>
          <span class="badge" :class="getTrendBadgeClass(comparisons.top_impression_rate)">
            {{ comparisons.top_impression_rate > 0 ? '↗' : '↘' }} {{ comparisons.top_impression_rate || 0 }}%
          </span>
          <div class="baseline">vs {{ (dashboard_data_prev.stats && dashboard_data_prev.stats.top_impression_rate) || '0.00' }}% last period</div>
        </div>

        <!-- 3. ABSOLUTE TOP IMPRESSION RATE -->
        <div class="card" title="During the selected date range, the percentage of your impressions that are shown as the very first ad in search results.">
          <div class="k">Absolute Top Impression Rate on Search</div>
          <div class="v">{{ (dashboard_data.stats && dashboard_data.stats.absolute_top_impression_rate) || '0.00' }}%</div>
          <span class="badge" :class="getTrendBadgeClass(comparisons.absolute_top_impression_rate)">
            {{ comparisons.absolute_top_impression_rate > 0 ? '↗' : '↘' }} {{ comparisons.absolute_top_impression_rate || 0 }}%
          </span>
          <div class="baseline">vs {{ (dashboard_data_prev.stats && dashboard_data_prev.stats.absolute_top_impression_rate) || '0.00' }}% last period</div>
        </div>
      </div>
    </template>

    <!-- ================================================================= -->
    <!-- 4. FOLLOW-UP, QUALITY & COVERAGE                                  -->
    <!-- ================================================================= -->
    <h2 class="rowlabel">Follow-up, quality &amp; coverage</h2>

    <!-- LOADING STATE FOR FOLLOW-UP, QUALITY & COVERAGE -->
    <div v-if="loading || loadingMessages" class="grid cards4 mb-6">
      <div v-for="i in 4" :key="'skel-fqc-' + i" class="card">
        <v-skeleton-loader type="article"></v-skeleton-loader>
      </div>
    </div>

    <div v-else class="grid cards4 mb-6">
      <!-- 1. FOLLOW-UPS SENT -->
      <div class="card">
        <div class="k">Follow-ups Sent</div>
        <div class="v">{{ totalFollowupsSent }}</div>
        <span class="badge info">Automated Outreach</span>
        <div class="baseline">Every lead answered with automatic follow-up nudges sent for you</div>
      </div>

      <!-- 2. LEAD QUALITY -->
      <!-- 2. LEAD QUALITY -->
      <div class="card">
        <div class="k">Lead Quality</div>
        <div class="v">{{ dashboard_data.lead_quality || '0.0' }}<small>/5</small></div>
        <span 
          class="badge" 
          :class="parseFloat(dashboard_data.lead_quality || 0) >= parseFloat(dashboard_data_prev.lead_quality || 0) ? 'up' : 'down'"
        >
          {{ parseFloat(dashboard_data.lead_quality || 0) >= parseFloat(dashboard_data_prev.lead_quality || 0) ? '↗' : '↘' }} 
          from {{ dashboard_data_prev.lead_quality || '0.0' }}
        </span>
        <div class="baseline">Scored from your submitted surveys</div>
      </div>

      <!-- 3. SURVEYS SUBMITTED -->
      <div class="card">
        <div class="k">Surveys Submitted</div>
        <div class="v">{{ surveysSubmittedCount }}</div>
        <span class="badge info">{{ surveysSubmittedCount }} of {{ dashboard_data.total_leads || 0 }} leads surveyed ({{ surveyPercentage }}%)</span>
        <div class="baseline">Every survey sharpens quality score — more surveys, truer score</div>
      </div>

      <!-- 4. SERVICE AREA DEFINED -->
      <div class="card">
        <div class="k">Service Area Defined</div>
        <template v-if="(dashboard_data.total_leads || 0) >= 50">
          <div class="v text-xl text-emerald-700 font-extrabold">Optimal</div>
          <span class="badge up">100% Configured</span>
          <div class="baseline">Service area settings active &amp; capturing leads efficiently across target radius</div>
        </template>

        <template v-else>
          <div class="v text-xl text-amber-700 font-extrabold">Partial</div>
          <span class="badge warn">Optimization Suggested</span>
          <div class="baseline">Confirm every city, county &amp; ZIP you cover so the radius tool can optimize</div>
          <router-link to="/settings/Radius" class="link cursor-pointer mt-1 inline-block">Review coverage →</router-link>
        </template>
      </div>
    </div>

    <!-- ================================================================= -->
    <!-- 5. BILLING, DISPUTES & REVIEWS                                    -->
    <!-- ================================================================= -->
    <h2 class="rowlabel">Billing &amp; reviews</h2>
    <div v-if="loading || loadingMessages" class="grid cards4 mb-6">
      <div v-for="i in 4" :key="'skel-bdr-' + i" class="card">
        <v-skeleton-loader type="article"></v-skeleton-loader>
      </div>
    </div>

    <div v-else class="grid cards4 mb-6">
      <div class="card">
        <div class="k">Charged Leads</div>
        <div class="v">{{ dashboard_data.charged_leads_total || 0 }}</div>
        <span class="badge info">Permanently Charged</span>
        <div class="baseline">vs {{ dashboard_data_prev.charged_leads_total || 0 }} last period</div>
      </div>

      <div class="card">
        <div class="k">Review Requests Sent</div>
        <div class="v">{{ dashboard_data.review_requests_sent || 0 }}</div>
        <span class="badge info">Sent via Text/Email</span>
        <div class="baseline">vs {{ dashboard_data_prev.review_requests_sent || 0 }} last period</div>
      </div>

      <div class="card">
        <div class="k">Review Links Clicked</div>
        <div class="v">{{ dashboard_data.review_links_clicked || 0 }}</div>
        <span class="badge info">Customer Engagements</span>
        <div class="baseline">Total clicks on review invitation links</div>
      </div>

      <div class="card">
        <div class="k">Reviews Gathered</div>
        <div class="v">{{ dashboard_data.review_gathered_count || 0 }}</div>
        <span class="badge up">Successfully Collected</span>
        <div class="baseline">Negative feedback count: {{ dashboard_data.negative_feedback_count || 0 }}</div>
      </div>
    </div>

    <!-- ================================================================= -->
    <!-- 6. TASK CARDS: DO THIS NOW                                        -->
    <!-- ================================================================= -->
    <h2 class="rowlabel">Do this now</h2>

    <!-- LOADING STATE FOR DO THIS NOW -->
    <div v-if="loading || loadingMessages" class="grid cards3 mb-6">
      <div v-for="i in 3" :key="'skel-dtn-' + i" class="card">
        <v-skeleton-loader type="article"></v-skeleton-loader>
      </div>
    </div>

    <div v-else class="grid cards3 mb-6">
      <!-- DYNAMIC TASK CARD FROM setUpNext OR DEFAULT FALLBACK -->
      <div class="card task" v-for="(taskItem, tIdx) in (setUpNext.length > 0 ? setUpNext : defaultTasks)" :key="'task-' + tIdx">
        <span class="tag" :class="taskItem.tagClass || 'urgent'">{{ taskItem.tagLabel || 'ACTION' }}</span>
        <div>
          <b>{{ taskItem.title }}</b>
          <p>{{ taskItem.description }}</p>

          <router-link v-if="taskItem.route" :to="taskItem.route" class="cta teal cursor-pointer mt-2 inline-block border-0 text-white">
            {{ taskItem.action_label || 'Set up' }}
          </router-link>

          <button v-else-if="taskItem.actionType === 'filter'" class="cta cursor-pointer mt-2 border-0 text-white" @click="filterMissedCalls()">
            {{ taskItem.action_label || 'Filter Missed' }}
          </button>

          <button v-else-if="taskItem.actionType === 'disputed'" class="cta teal cursor-pointer mt-2 border-0 text-white" @click="filterDisputedLeads">
            {{ taskItem.action_label || 'See disputed leads' }}
          </button>

          <div v-else-if="taskItem.actionType === 'reports'" class="flex gap-2 mt-2">
            <a class="cta teal cursor-pointer text-white border-0" @click="getMonthlyReport">Open Report</a>
            <a class="cta bg-slate-700 cursor-pointer text-white border-0" @click="sendMonthlyReport">Send Email</a>
          </div>
        </div>
      </div>
    </div>

    <!-- ================================================================= -->
    <!-- FILTER TOOLBAR BOX (Placed above Trends & Leads)                  -->
    <!-- ================================================================= -->
    <div class="mb-3" v-if="me"><video-guide ids="lead-status,qualification,sources" label="Lead management guides" /></div>
    <div class="card mb-6 filters-panel-card">
      <div class="filters-row flex items-center flex-wrap">
        <!-- SOURCE FILTER -->
        <div class="filter-item" style="position: relative;">
          <v-select
            outlined
            dense
            hide-details
            label="Source"
            class="white bg-white rounded filter-select-input"
            :items="sourceFilterOptions"
            item-text="text"
            item-value="value"
            v-model="source_filter"
            clearable
            @change="onFilterChange()"
            style="width: 200px;"
          >
            <template v-slot:selection="{ item }">
              <div class="d-flex align-center">
                <img v-if="item.icon" :src="item.icon" style="width: 15px; height: 15px; object-fit: contain; margin-right: 5px;" />
                <span class="text-xs">{{ item.text }}</span>
              </div>
            </template>
            <template v-slot:item="{ item }">
              <div class="d-flex align-center py-1">
                <img v-if="item.icon" :src="item.icon" style="width: 16px; height: 16px; object-fit: contain; margin-right: 7px;" />
                <span class="text-xs">{{ item.text }}</span>
              </div>
            </template>
          </v-select>
        </div>

        <!-- LEAD TYPE FILTER -->
        <div class="filter-item">
          <v-select
            outlined
            dense
            hide-details
            label="Lead Type"
            class="white bg-white rounded filter-select-input"
            :items="['Show All', 'All Except Robocalls', 'High Value', 'Converted', 'In Contact', 'AI Leads', 'Missed Calls', 'Robocalls', 'Disputed']"
            v-model="filter_calls_type"
            clearable
            @change="onFilterChange()"
            style="width: 200px;"
          ></v-select>
        </div>

        <!-- LEAD QUALITY FILTER -->
        <div class="filter-item">
          <v-select
            outlined
            dense
            hide-details
            label="Quality"
            class="white bg-white rounded filter-select-input"
            :items="[5, 4, 3, 2, 1]"
            v-model="lead_quality_filter"
            clearable
            @change="onFilterChange()"
            style="width: 130px;"
          ></v-select>
        </div>

        <!-- AGENT PERFORMANCE FILTER -->
        <div class="filter-item">
          <v-select
            outlined
            dense
            hide-details
            label="Performance"
            class="white bg-white rounded filter-select-input"
            :items="[5, 4, 3, 2, 1]"
            v-model="performance_filter"
            clearable
            @change="onFilterChange()"
            style="width: 150px;"
          ></v-select>
        </div>

        <!-- DOWNLOAD CSV BUTTON -->
        <div class="filter-item ml-auto">
          <button
            v-if="me"
            class="download-csv-btn flex items-center px-4 py-2 rounded-lg font-bold text-xs bg-white border border-slate-300 text-slate-700 shadow-sm hover:bg-slate-50 hover:border-[#2fa7b8] transition-all hover:shadow cursor-pointer"
            @click="downloadCsv"
            title="Download filtered leads as CSV"
            style="height: 40px;"
          >
            <i class="mdi mdi-tray-arrow-down text-base text-[#2fa7b8] mr-2"></i>
            <span>Download CSV</span>
          </button>
        </div>
      </div>
    </div>

    <conversion-stats :stats="dashboard_data.conversions_stats" />
    <ghl-conversion-sync v-if="me" />
    <!-- ================================================================= -->
    <!-- TRENDS — DYNAMICALLY BOUND TO PIPELINE CHART DATA                 -->
    <!-- ================================================================= -->
    <div class="float-right">
      <v-switch
        v-model="expanded_chart"
        label="Expanded"
        color="success"
        inset
        dense
        hide-details
        class="ma-0 pa-0 text-xs font-bold"
      />
    </div>

    <div v-show="expanded_chart" class="float-right mr-2 -mt-2">
      <v-select
        outlined
        dense
        hide-details
        label=""
        class="white header-inputs"
        :items="['Frequency: Auto', 'Daily', 'Weekly', 'Monthly']"
        v-model="selected_frequency_chart"
        @change="loadDashboard2()"
        style="width: 175px;"
      ></v-select>
    </div>

    <h2 class="rowlabel">
      Trends — since you started with us
    </h2>

    <div style="clear: both; padding-top: 10px;"></div>

    <div class="card">
      <div v-show="!expanded_chart" class="trio">

        <!-- COLUMN 1: LEAD VOLUME (DYNAMIC) -->
        <div class="mini">
          <h4>Lead Volume <span style="color:#c6d0d9">/ week</span></h4>
          <div class="now">
            {{ calculatedWeeklyAvgLeads }}
            <span style="font-size:12px;color:#8593a3;font-weight:700">avg this month</span>
          </div>

          <svg viewBox="0 0 300 110" width="100%" height="110">
            <line x1="4" y1="90" x2="296" y2="90" stroke="#eef2f5"/>
            
            <!-- CHART 1: LEAD VOLUME (AUTO-SCALED) -->
            <polyline
              fill="none"
              stroke="#2fa7b8"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              :points="buildSparklinePoints(leadVolumeData)"
            />

            <circle
              :cx="getSparklineEndpoint(leadVolumeData).x"
              :cy="getSparklineEndpoint(leadVolumeData).y"
              r="3.5"
              fill="#2fa7b8"
            />

            <!-- Dynamic Axis Labels -->
            <text x="8" y="104" font-size="10" fill="#8593a3">{{ chartStartMonthLabel }}</text>
            <text x="272" y="104" font-size="10" fill="#8593a3">{{ chartEndMonthLabel }}</text>
          </svg>
        </div>

        <!-- COLUMN 2: ANSWERED IN <30s (DYNAMIC) -->
        <div class="mini">
          <h4>Answered in &lt;30s</h4>
          <div class="now">
            {{ answeredUnder30sRate }}%
            <span style="font-size:12px;color:#1d7a46;font-weight:700">
              ↗ from {{ previousAnsweredUnder30sRate }}%
            </span>
          </div>

          <svg viewBox="0 0 300 110" width="100%" height="110">
            <line x1="4" y1="90" x2="296" y2="90" stroke="#eef2f5"/>

            <!-- CHART 2: ANSWERED IN <30s (SCALED 0-100%) -->
            <polyline
              fill="none"
              stroke="#16283c"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              :points="buildSparklinePoints(under30sData, 0, 100)"
            />

            <!-- Dynamic Endpoint Dot -->
            <circle
              :cx="getSparklineEndpoint(under30sData, 0, 100).x"
              :cy="getSparklineEndpoint(under30sData, 0, 100).y"
              r="3.5"
              fill="#16283c"
            />

            <!-- Dynamic AI Started Annotation Line & Label -->
            <g v-if="ai_started_on">
              <line
                :x1="getAiStartedXPosition()"
                y1="20"
                :x2="getAiStartedXPosition()"
                y2="90"
                stroke="#6c63c8"
                stroke-dasharray="4 3"
                stroke-width="1.5"
              />
              <text
                :x="parseFloat(getAiStartedXPosition()) + 4"
                y="18"
                font-size="9.5"
                fill="#6c63c8"
                font-weight="700"
              >AI answering on</text>
            </g>

            <!-- Dynamic Axis Labels -->
            <text x="8" y="104" font-size="10" fill="#8593a3">{{ chartStartMonthLabel }}</text>
            <text x="272" y="104" font-size="10" fill="#8593a3">{{ chartEndMonthLabel }}</text>
          </svg>
        </div>

        <!-- COLUMN 3: LEAD QUALITY (DYNAMIC WITH DIRECTION & COLOR FIX) -->
        <div class="mini">
          <h4>Lead Quality <span style="color:#c6d0d9">/ 5</span></h4>
          <div class="now">
            {{ dashboard_data.lead_quality || '0.0' }}
            <span 
              style="font-size:12px;font-weight:700;" 
              :style="{ color: parseFloat(dashboard_data.lead_quality || 0) >= parseFloat(dashboard_data_prev.lead_quality || 0) ? '#1d7a46' : '#c0392b' }"
            >
              {{ parseFloat(dashboard_data.lead_quality || 0) >= parseFloat(dashboard_data_prev.lead_quality || 0) ? '↗' : '↘' }} 
              from {{ dashboard_data_prev.lead_quality || '0.0' }}
            </span>
          </div>

          <svg viewBox="0 0 300 110" width="100%" height="110">
            <line x1="4" y1="90" x2="296" y2="90" stroke="#eef2f5"/>

            <!-- Dynamic Polyline (0-5 Scale) -->
            <polyline
              fill="none"
              stroke="#6c63c8"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              :points="buildSparklinePoints(leadQualityData, 0, 5)"
            />

            <!-- Dynamic Endpoint Dot -->
            <circle
              :cx="getSparklineEndpoint(leadQualityData, 0, 5).x"
              :cy="getSparklineEndpoint(leadQualityData, 0, 5).y"
              r="3.5"
              fill="#6c63c8"
            />

            <!-- Dynamic Axis Labels -->
            <text x="8" y="104" font-size="10" fill="#8593a3">{{ chartStartMonthLabel }}</text>
            <text x="272" y="104" font-size="10" fill="#8593a3">{{ chartEndMonthLabel }}</text>
          </svg>
        </div>

      </div>

      <!-- <div class="chartnote">
        Just the three numbers that matter — lead volume, answer speed, lead quality — across the whole client history. One scale per chart. Everything else lives in an Advanced view.
      </div> -->

      <div v-show="expanded_chart" class="pl-3 pt-3">
        <div class="-mt-5 -ml-4 -mb-2">
            <apexchart
                :options="lineChartMetricsOptions"
                :series="lineChartMetricsSeries"
                type="line"
                height="375"
                ref="apexChart"
                v-if="metricsChartReady"
            />

            <div class="custom-legend centered">
                <div
                  v-for="item in legendItems"
                  :key="item.name"
                  class="custom-legend-item"
                  :class="{
                    disabled: item.disabled,
                    dimmed: hoveredLegend && hoveredLegend !== item.name
                  }"
                  @mouseover="handleLegendHover(item)"
                  @mouseleave="handleLegendLeave"
                  @click="toggleSeries(item)"
                >
                  <v-tooltip top color="primary">
                    <template #activator="{ on, attrs }">
                      <div class="legend-content" v-bind="attrs" v-on="on">
                        <span
                          class="legend-dot"
                          :style="{ backgroundColor: item.color }"
                        ></span>

                        <span class="legend-label">
                          {{ item.name }}
                        </span>

                        <span class="legend-value">
                          {{ item.value }}
                        </span>
                      </div>
                    </template>

                    <span>{{ item.description }}</span>
                  </v-tooltip>
                </div>
              </div>
        </div>
      </div>
    </div>

    <!-- ID for smooth scrolling -->
    <div id="recent-leads-section"></div>

    <h2 class="rowlabel">Recent leads</h2>
    <div class="card p-4 mb-8">
      <!-- HEADER TOOLS -->
      <div class="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <span class="bg-[#FFF7CC] border border-[#E6D98A] text-slate-700 text-xs font-bold px-2.5 py-1 rounded">
            Yellow Highlighted Leads — New leads from last 2 weeks
          </span>
        </div>

        <div class="flex items-center gap-3 flex-wrap">
          <div class="legend-items">
            <div v-for="item in legend" :key="item.value" class="legend-item">
              <span class="legend-badge" :class="item.class">{{ item.value }}</span>
              <span class="legend-label">{{ item.label }}</span>
            </div>
          </div>

        </div>
      </div>

      <div v-show="selected.length > 0" class="mb-4">
        <button class="rounded text-blue text-xs font-bold border border-blue px-3 py-1.5" @click="trainAgentWithSelectedCalls()">
          Train Agent with selected calls ({{ selected.length }})
        </button>
      </div>
    </div>

    <v-skeleton-loader v-if="loadingMessages" type="article, article, article"></v-skeleton-loader>

      <div v-else-if="filteredLeads.length === 0" class="text-center py-12 text-slate-500">
        <i class="mdi mdi-phone-off text-4xl text-slate-300 block mb-2"></i>
        <b class="text-slate-700 block text-sm">No Leads Found</b>
        <span class="text-xs">No leads found for the given period. Refine your search or wait for new inquiries.</span>
      </div>

    <!-- RECENT LEADS ITEM CARDS (MOCKUP ACCURATE) -->
    <div v-else>
      <!-- LEADS LIST LOOP (SWAPPED TO filteredLeads) -->
      <div class="rg-lead-list">
        <div
          v-for="item in filteredLeads"
          :key="item.id"
          class="card lead-row clearfix"
          :class="[
            getRowClass(item),
            activeLead && activeLead.id === item.id ? 'is-active' : '',
            { 'lead--checked': !!item.checked_at }
          ]"
          role="button"
          tabindex="0"
          @click="openLeadDialog(item)"
          @keydown.enter.prevent="openLeadDialog(item)"
          @keydown.space.prevent="openLeadDialog(item)"
          :data-lead-id="item.id"
        >
          <!-- LEFT SIDE: TITLE + ICONS + SUB METADATA -->
          <div class="lead-left">
            <div class="lead-title-line">
              <i :class="leadTypeIcon(item)" class="lead-type-icon"></i>
              <b class="lead-name">
                <span v-if="item.customer_name">{{ item.customer_name }} — </span>
                <span v-if="item.job_type">{{ item.job_type }} — </span>
                <span>{{ item.ai_provided_phone_number || item.customer_phone_number || item.tracking_number || 'No phone number' }}</span>
              </b>

              <!-- PROVIDER ICONS (INLINE WITH TITLE) -->
              <span class="provider-icons" @click.stop>
                <v-tooltip v-for="p in normalizedProviders(item)" :key="p.key" top color="primary">
                  <template #activator="{ on, attrs }">
                    <img v-bind="attrs" v-on="on" :src="p.src" :alt="p.title" class="channel-icon" style="cursor: initial; width: 20px; height: 20px; object-fit: contain; margin-right: 2px;" />
                  </template>
                  <span>{{ p.title }}</span>
                </v-tooltip>

                <v-tooltip v-if="!item.sources || (item.sources.indexOf('bland') === -1 && item.sources.indexOf('web') === -1 && item.sources.indexOf('lead_form') === -1)" top color="primary">
                  <template #activator="{ on, attrs }">
                    <i class="mdi mdi-account human-icon" v-bind="attrs" v-on="on"></i>
                  </template>
                  <span>This lead was answered by a human</span>
                </v-tooltip>
              </span>
            </div>

            <!-- SECOND LINE: DATE & LEAD ID -->
            <div class="lead-sub-line">
              <span>{{ displayLeadCreationDate(item) }}</span>
              <span> • Lead ID: {{ item.lead_id || item.id }}</span>
              <span v-if="item.is_lsa_booked" class="rg-pill">Booked</span>
              <span v-if="item.is_new_lead" class="rg-pill rg-pill--warn">New</span>
            </div>
          </div>

          <!-- RIGHT SIDE: BADGES + TOOLTIPS + ACTIONS TOOLBAR -->
          <div class="lead-right" @click.stop>

            <!-- 1. CHARGE & DISPUTE STATUS BADGES (PRESERVED DISTINCT COLORS) -->
            <template v-if="item.charge_status == 'CHARGED'">
              <span v-if="item.dispute_status == 'APPROVED'" class="badge charge-credited">
                Credited
              </span>
              <span v-else class="badge charge-charged">
                Charged
              </span>
            </template>
            <template v-else>
              <span class="badge charge-not-charged">
                Not Charged
              </span>
            </template>

            <converted-badge v-if="item.is_converted == 1 || item.conversion_status === 'in_contact'" :value="item.is_converted" :status="item.conversion_status" />
            <!-- SPAM/HighValue/DroppedCall BADGES -->
            <span v-if="item.tag && item.tag.includes('Spam')" class="badge score-5">
              Blocked / Robocall
            </span>
            <span v-else-if="item.is_high_value == 1" class="badge score-1">
              High Value
            </span>
            <span v-else-if="isDroppedCall(item)" class="badge score-3">
              Dropped Call
            </span>

            <!-- 2. SATISFACTION RATING BADGE WITH TOOLTIP -->
            <template v-if="item.rated_at">
              <v-tooltip bottom content-class="intake-tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <span
                    v-bind="attrs"
                    v-on="on"
                    class="badge"
                    :class="getSatisfactionScoreClass(item.satisfaction)"
                  >
                    Rated {{ convertSatisfactionToNumber(item.satisfaction) }} · {{ satisfactionLabelByTexts(item.satisfaction) }}
                  </span>
                </template>
                <span class="tooltip-text">
                  Google LSA Rating Notes: {{ item.additional_comments || item.summary || 'No notes available' }}
                </span>
              </v-tooltip>
            </template>

            <!-- INTAKE PERFORMANCE (IP) WITH DYNAMIC SCORE COLORING & TOOLTIP -->
          <template v-if="item.intake_performance">
            <v-tooltip bottom content-class="intake-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  class="badge"
                  :class="getPerformanceScoreClass(item.intake_performance.rating)"
                >
                  IP {{ item.intake_performance.rating }}/5
                </span>
              </template>
              <span class="tooltip-text">
                Intake Performance: {{ item.intake_performance.explanation || 'No details available' }}
              </span>
            </v-tooltip>
          </template>

          <!-- AI PERFORMANCE (AIP) WITH DYNAMIC SCORE COLORING & TOOLTIP -->
          <template v-if="item.ai_performance">
            <v-tooltip bottom content-class="intake-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  class="badge"
                  :class="getPerformanceScoreClass(item.ai_performance.rating)"
                >
                  AIP {{ item.ai_performance.rating }}/5
                </span>
              </template>
              <span class="tooltip-text">
                AI Performance: {{ item.ai_performance.explanation || 'No details available' }}
              </span>
            </v-tooltip>
          </template>

            <!-- REVIEWS STATUS BADGES -->
            <span v-if="item.review_request_sent == 1 && !item.review_gathered" class="badge score-4">
              Review Request Sent
            </span>
            <span v-if="item.review_request_sent == 1 && item.review_gathered" class="badge score-1">
              Review Gathered!
            </span>

            <!-- ACTION BUTTONS TOOLBAR -->
            <span class="action-buttons">
              <!-- NOTES POPUP -->
              <button class="icon-link relative" type="button" @click.stop="activeLead = item; show_notes_dialog = true;" title="Notes">
                <i class="mdi mdi-note-text-outline"></i>
                <span v-if="item.notes && item.notes.length > 0" class="note-counter">{{ item.notes.length }}</span>
              </button>

              <!-- CHECKBOX -->
              <button class="icon-link" type="button" @click.stop="toggleChecked(item)" :title="item.checked_at ? 'Uncheck lead' : 'Mark as checked'">
                <i class="mdi" :class="item.checked_at ? 'mdi-checkbox-marked-outline text-emerald-600' : 'mdi-checkbox-blank-outline'"></i>
              </button>

              <!-- STAR RATING MENU -->
              <v-menu v-model="ratingMenu[item.id]" :close-on-content-click="false" offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <button class="icon-link" type="button" v-bind="attrs" v-on="on" @click.stop="openRatingMenu(item)" title="Rate Lead Quality">
                    <i class="mdi" :class="item.quality_rating ? 'mdi-star text-amber-500' : 'mdi-star-outline'"></i>
                  </button>
                </template>
                <v-card class="pa-4" width="280">
                  <div class="text-subtitle-2 font-weight-bold mb-2">Rate Lead Quality</div>
                  <div class="d-flex align-center justify-center mb-3">
                    <v-rating v-model="selectedRatings[item.id]" color="amber" background-color="grey lighten-1" hover length="5" size="24"></v-rating>
                  </div>
                  <v-textarea
                    v-model="selectedRatingNotes[item.id]"
                    :label="selectedRatings[item.id] === 5 ? 'Why is this a high value lead?' : 'Reason for rating'"
                    outlined dense rows="3" hide-details class="mb-3"
                  ></v-textarea>
                  <div class="d-flex justify-end gap-2">
                    <v-btn text small @click="ratingMenu[item.id] = false">Cancel</v-btn>
                    <v-btn color="primary" small :loading="savingRating[item.id]" @click="submitRating(item)">Save</v-btn>
                  </div>
                </v-card>
              </v-menu>

              <!-- THREE DOTS MENU -->
              <v-menu bottom right nudge-bottom="25">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" :ripple="false" small class="dots-btn">mdi-dots-vertical</v-icon>
                </template>
                <v-list class="table-menu-actions">
                  <v-list-item class="hover:bg-slate-100">
                    <v-list-item-title v-show="item.archived == 0">
                      <a href="javascript:void(0);" @click="archiveLead(item)" class="text-slate-800 text-xs font-bold"><i class="mdi mdi-archive-outline mr-1"></i> Archive</a>
                    </v-list-item-title>
                    <v-list-item-title v-show="item.archived == 1">
                      <a href="javascript:void(0);" @click="unArchiveLead(item)" class="text-slate-800 text-xs font-bold"><i class="mdi mdi-package-up mr-1"></i> Unarchive</a>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item class="hover:bg-slate-100">
                    <v-list-item-title>
                      <a href="javascript:void(0);" @click="addLeadToKnowledgebase(item)" class="text-slate-800 text-xs font-bold"><i class="mdi mdi-plus mr-1"></i> Add to Knowledgebase</a>
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- ================================================================= -->
    <!-- 9. MODALS & DIALOGS (ALL PRESERVED)                              -->
    <!-- ================================================================= -->

    <!-- DIALOG 7: FULL LEAD DETAILS TABBED OVERLAY MODAL -->
    <div v-if="leadDialogOpen" class="rg-modal-overlay" @click.self="closeLeadDialog" :key="showFullactiveLeadKey">
        <div class="rg-modal" role="dialog" aria-modal="true">
            <div class="rg-modal__header">
                <div>
                    <div class="rg-modal__title">
                        <span v-if="activeLead && activeLead.job_type">{{ activeLead.job_type }}</span>
                        <span v-if="activeLead && activeLead.customer_name"> - {{ activeLead.customer_name }}</span>
                        <span v-if="activeLead && (activeLead.ai_provided_phone_number || activeLead.customer_phone_number || activeLead.tracking_number)"> - {{ activeLead.ai_provided_phone_number || activeLead.customer_phone_number || activeLead.tracking_number || 'No phone number' }}</span>
                        <high-value-badge v-if="activeLead" class="ml-2" :value="activeLead.is_high_value" aria-live="polite" />
                        <converted-badge v-if="activeLead && (activeLead.is_converted == 1 || activeLead.conversion_status === 'in_contact')" class="ml-2" :value="activeLead.is_converted" :status="activeLead.conversion_status" />
                    </div>
                    <div class="rg-modal__subtitle" v-if="activeLead">
                        <span class="rg-muted">{{ displayLeadCreationDate(activeLead) }}</span>
                        <span class="rg-dot">•</span>
                        <span class="rg-muted">Lead ID: {{ activeLead.lead_id || activeLead.id }}</span>
                    </div>
                </div>

                <button class="rg-close" type="button" @click="closeLeadDialog" aria-label="Close">
                    <i class="mdi mdi-close"></i>
                </button>
            </div>

            <div class="rg-modal__tabs">
                <button class="rg-tab" :class="{active: leadDialogTab==='overview'}" type="button" @click="leadDialogTab='overview'">Overview</button>
                <button v-if="me" class="rg-tab" :class="{active: leadDialogTab==='qualification'}" type="button" @click="leadDialogTab='qualification'">Qualification</button>
                <button class="rg-tab" :class="{active: leadDialogTab==='notes'}" type="button" @click="leadDialogTab='notes'">Notes</button>
                <button class="rg-tab" :class="{active: leadDialogTab==='transcripts'}" type="button" @click="leadDialogTab='transcripts'">Transcript</button>
                <button class="rg-tab" :class="{active: leadDialogTab==='photos'}" type="button" @click="leadDialogTab='photos'">Photos</button>
            </div>

            <div class="rg-modal__body" v-if="activeLead">
                <div v-show="leadDialogTab==='qualification'">
                    <lead-survey-instructions v-if="me" :key="activeLead.id" :lead="activeLead" @saved="onSurveyInstructionsSaved" @conversion-saved="onConversionSaved" />
                </div>
                <!-- Overview -->
                <div v-show="leadDialogTab==='overview'" class="rg-grid">

                    <div class="rg-kv">
                        <div class="rg-k">Phone Number</div>
                        <div class="rg-v">
                            <strong style="font-size: 16px;"><a :href="'tel:' + (activeLead.ai_provided_phone_number || activeLead.customer_phone_number || activeLead.tracking_number || 'Unknown')"><i class="mdi mdi-phone" style="font-size: 16px;"></i> &nbsp;{{ activeLead.ai_provided_phone_number || activeLead.customer_phone_number || activeLead.tracking_number || 'No phone number' }}</a></strong>
                        </div>
                    </div>

                    <div class="rg-kv">
                        <div class="rg-k">Name</div>
                        <div class="rg-v">
                            <div :class="'lsa-show-customer-name lsa-show-customer-name-' + activeLead.id">
                                <a href="javascript: void(0);" @click="showEditCustomerName(activeLead)">{{ activeLead.customer_name || "Add Name" }}<i class="mdi mdi-pencil-outline ml-2"></i></a>
                            </div>
                            <div :class="'lsa-edit-customer-name lsa-edit-customer-name-' + activeLead.id" style="display: none; position: relative; width: 140px;">
                                <input type="text" :value="activeLead.customer_name" style="padding: 5px; border: solid 1px #CCCCCC; width: 100px;" />
                                <div style="position: absolute; margin-top: -22px; right: -31px;">
                                    <a href="javascript: void(0);" style="color: #3EBB80 !important;" @click="editCustomerName(activeLead)"><i class="mdi mdi-check"></i></a>
                                    <a href="javascript: void(0);" class="" style="color: #E53F71 !important;" @click="cancelEditCustomerName(activeLead)"><i class="mdi mdi-close"></i></a>
                                </div>
                                <div style="clear: both;"></div>
                            </div>
                        </div>
                    </div>

                    <div class="rg-kv">
                        <div class="rg-k">Email</div>
                        <div class="rg-v">{{ activeLead.customer_email || "N/A" }}</div>
                    </div>

                    <div class="rg-kv">
                        <div class="rg-k">Address</div>
                        <div class="rg-v">{{ activeLead.customer_address || "N/A" }}</div>
                    </div>

                    <div class="rg-kv">
                        <div class="rg-k">Charge</div>
                        <div class="rg-v">
                            <span v-if="activeLead.charge_status == 'CHARGED'">
                                <span v-if="activeLead.dispute_status == 'APPROVED'">
                                    <v-chip
                                        small
                                        color="green lighten-4"
                                        text-color="green lighten-1"
                                    >
                                        Credited
                                    </v-chip>
                                </span>
                                <span v-else>
                                    <v-chip
                                        small
                                        color="blue lighten-4"
                                        text-color="blue lighten-1"
                                    >
                                        Charged
                                    </v-chip>
                                </span>
                            </span>
                            <span v-else>
                                <v-chip
                                    small
                                    color="gray lighten-5"
                                    text-color="gray lighten-1"
                                >
                                    Not Charged
                                </v-chip>
                            </span>
                        </div>
                    </div>

                    <div class="rg-kv">
                        <div class="rg-k">Lead type</div>
                        <div class="rg-v">{{ leadTypeLabel(activeLead) }}</div>
                    </div>

                    <div class="rg-kv" v-if="me">
                        <div class="rg-k">Tags</div>
                        <div class="rg-v">
                            <div v-if="me" class="rg-tags" @click.stop style="margin-top:8px;">
                                <span v-for="(tag, index_tag) in activeLead.tag" :key="'tag-'+index_tag" class="pr-2 pb-1 inline-block">
                                  <v-chip v-if="tag == 0" class="mb-1 dark">
                                    <span style="font-size: 12px;">Not Set</span>
                                  </v-chip>

                                  <v-chip class="mb-1" v-if="tag == 1" color="#3EBB80">
                                    <span style="font-size: 12px; color: #FFFFFF;">Existing Customer</span>
                                    <v-icon class="tags" style="color: #FFFFFF; cursor: pointer;" @click.stop="removeTag(activeLead, tag);">mdi-delete-outline</v-icon>
                                  </v-chip>

                                  <v-chip class="mb-1" v-if="tag == 2" color="#44468F">
                                    <span style="font-size: 12px; color: #FFFFFF;">Missed Call</span>
                                    <v-icon class="tags" style="color: #FFFFFF; cursor: pointer;" @click.stop="removeTag(activeLead, tag);">mdi-delete-outline</v-icon>
                                  </v-chip>

                                  <v-chip class="mb-1" v-if="tag == 3" color="#FFB443" v-show="!isOnlyAi()">
                                    <span style="font-size: 12px; color: #FFFFFF;">Opportunity</span>
                                    <v-icon class="tags" style="color: #FFFFFF; cursor: pointer;" @click.stop="removeTag(activeLead, tag);">mdi-delete-outline</v-icon>
                                  </v-chip>

                                  <v-chip class="mb-1" v-if="tag == 4" color="#2477F6" v-show="!isOnlyAi()">
                                    <span style="font-size: 12px; color: #FFFFFF;">Meeting Scheduled</span>
                                    <v-icon class="tags" style="color: #FFFFFF; cursor: pointer;" @click.stop="removeTag(activeLead, tag);">mdi-delete-outline</v-icon>
                                  </v-chip>

                                  <v-chip class="mb-1" v-if="tag == 5" color="#FF3349">
                                    <span style="font-size: 12px; color: #FFFFFF;">Not Interested</span>
                                    <v-icon class="tags" style="color: #FFFFFF; cursor: pointer;" @click.stop="removeTag(activeLead, tag);">mdi-delete-outline</v-icon>
                                  </v-chip>

                                  <v-chip class="mb-1" v-if="tag == 6" color="#157B79">
                                    <span style="font-size: 12px; color: #FFFFFF;">Quote Requested</span>
                                    <v-icon class="tags" style="color: #FFFFFF; cursor: pointer;" @click.stop="removeTag(activeLead, tag);">mdi-delete-outline</v-icon>
                                  </v-chip>

                                  <v-chip class="mb-1" v-if="tag == 7" color="#6E62B5">
                                    <span style="font-size: 12px; color: #FFFFFF;">Duplicate Call</span>
                                    <v-icon class="tags" style="color: #FFFFFF; cursor: pointer;" @click.stop="removeTag(activeLead, tag);">mdi-delete-outline</v-icon>
                                  </v-chip>

                                  <v-chip class="mb-1" v-if="tag == 7" color="#338108">
                                    <span style="font-size: 12px; color: #FFFFFF;">Assisted Signing</span>
                                    <v-icon class="tags" style="color: #FFFFFF; cursor: pointer;" @click.stop="removeTag(activeLead, tag);">mdi-delete-outline</v-icon>
                                  </v-chip>

                                  <v-chip class="mb-1" v-if="tag == 8" color="#E67B20">
                                    <span style="font-size: 12px; color: #FFFFFF;">AI Test Call</span>
                                    <v-icon class="tags" style="color: #FFFFFF; cursor: pointer;" @click.stop="removeTag(activeLead, tag);">mdi-delete-outline</v-icon>
                                  </v-chip>

                                  <v-chip class="mb-1" v-if="tag == 9" color="#4FBFA9">
                                    <span style="font-size: 12px; color: #FFFFFF;">Call Back Later</span>
                                    <v-icon class="tags" style="color: #FFFFFF; cursor: pointer;" @click.stop="removeTag(activeLead, tag);">mdi-delete-outline</v-icon>
                                  </v-chip>

                                  <v-chip class="mb-1"
                                    v-if="tag != 0 && tag != 1 && tag != 2 && tag != 3 && tag != 4 && tag != 5 && tag != 6 && tag != 7 && tag != 8 && tag != 9
                                      && tag != 'Booked to Estimate'
                                      && tag != 'Booked to Service'
                                      && tag != 'Booked for Appointment'
                                      && tag != 'Complete'
                                      && tag != 'Incomplete'
                                      && tag != 'Cancelled'
                                      && tag != 'Duplicate Call'
                                      && tag != 'Assisted Signing'
                                      && tag != 'AI Test Call'
                                    "
                                    :color="getCustomTagColor(tag)"
                                  >
                                    <span style="font-size: 12px; color: #FFFFFF;">{{ tag }}</span>
                                    <v-icon class="tags" style="color: #FFFFFF; cursor: pointer;" @click.stop="removeTag(activeLead, tag);">mdi-delete-outline</v-icon>
                                  </v-chip>
                                </span>

                                <v-menu bottom right nudge-bottom="25">
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-icon color="green" v-bind="attrs" v-on="on" :ripple="false" small class="ml-1">mdi-plus</v-icon>
                                  </template>
                                  <v-list class="table-menu-actions p-2" style="max-height: 320px; overflow-y: auto;">
                                    <v-list-item class="hover:bg-gray-200"><v-list-item-title><a href="javascript: void(0);"><span class="table-menu-action-icon" style="color: #44468F;" @click="setTag(activeLead, 2);">Missed Call</span></a></v-list-item-title></v-list-item>
                                    <v-list-item class="hover:bg-gray-200" v-show="!isOnlyAi()"><v-list-item-title><a href="javascript: void(0);"><span class="table-menu-action-icon" style="color: #FFB443;" @click="setTag(activeLead, 3);">Opportunity</span></a></v-list-item-title></v-list-item>
                                    <v-list-item class="hover:bg-gray-200" v-show="!isOnlyAi()"><v-list-item-title><a href="javascript: void(0);"><span class="table-menu-action-icon" style="color: #2477F6;" @click="setTag(activeLead, 4);">Meeting Scheduled</span></a></v-list-item-title></v-list-item>
                                    <v-list-item class="hover:bg-gray-200"><v-list-item-title><a href="javascript: void(0);"><span class="table-menu-action-icon" style="color: #FF3349;" @click="setTag(activeLead, 5);">Not Interested</span></a></v-list-item-title></v-list-item>
                                    <v-list-item class="hover:bg-gray-200"><v-list-item-title><a href="javascript: void(0);"><span class="table-menu-action-icon" style="color: #3EBB80;" @click="setTag(activeLead, 1);">Existing Customer</span></a></v-list-item-title></v-list-item>
                                    <v-list-item class="hover:bg-gray-200"><v-list-item-title><a href="javascript: void(0);"><span class="table-menu-action-icon" style="color: #157B79;" @click="setTag(activeLead, 6);">Quote Requested</span></a></v-list-item-title></v-list-item>
                                    <v-list-item class="hover:bg-gray-200"><v-list-item-title><a href="javascript: void(0);"><span class="table-menu-action-icon" style="color: #6E62B5;" @click="setTag(activeLead, 7);">Duplicate Call</span></a></v-list-item-title></v-list-item>
                                    <v-list-item class="hover:bg-gray-200"><v-list-item-title><a href="javascript: void(0);"><span class="table-menu-action-icon" style="color: #338108;" @click="setTag(activeLead, 'Assisted Signing');">Assisted Signing</span></a></v-list-item-title></v-list-item>
                                    <v-list-item class="hover:bg-gray-200"><v-list-item-title><a href="javascript: void(0);"><span class="table-menu-action-icon" style="color: #E67B20;" @click="setTag(activeLead, 8);">AI Test Call</span></a></v-list-item-title></v-list-item>
                                    <v-list-item class="hover:bg-gray-200"><v-list-item-title><a href="javascript: void(0);"><span class="table-menu-action-icon" style="color: #4FBFA9;" @click="setTag(activeLead, 9);">Call Back Later</span></a></v-list-item-title></v-list-item>
                                  </v-list>
                                </v-menu>
                            </div>
                        </div>
                    </div>

                    <div class="rg-kv">
                        <div class="rg-k">Notes</div>
                        <div class="rg-v">
                            <div class="rg-note-actions">

                                <div style="font-size: 12px; padding-bottom: 10px;">
                                    <div v-if="activeLead.notes.length == 0">
                                        No notes found.
                                    </div>

                                    <div v-else>
                                        <div v-for="(note, note_index) in activeLead.notes" :key="'note-' + note_index" style="clear: both; padding-bottom: 10px;">
                                            <div style="float: left; width: calc(100% - 220px);">
                                                {{ note.note }}
                                            </div>

                                            <div style="float: right; width: 200px; text-align: right;">
                                                [{{ formatDate(note.timestamp) }}]

                                                &nbsp;&nbsp;

                                                <a href="javascript: void(0);"><i class="mdi mdi-delete-outline" style="color: red;" @click="deleteNote(note_index)"></i></a>
                                            </div>

                                            <div style='clear: both;'></div>
                                        </div>

                                        <div style='clear: both;'></div>
                                    </div>
                                </div>

                                <v-expansion-panels v-model="panel" accordion flat dense>
                                    <v-expansion-panel>
                                      <v-expansion-panel-header>Add Note</v-expansion-panel-header>
                                      <v-expansion-panel-content>
                                        <v-textarea
                                            clearable
                                            outlined
                                            dense
                                            hide-details
                                            label="Enter Note"
                                            v-model="new_note"

                                            class="white border border-white mt-2"
                                        ></v-textarea>

                                        <button class="rg-dialog-button-save rounded text-white text-xs text-bold focus:outline-none py-2 px-4 float-right" :class="{'spinner': savingNote}" @click="saveNote">
                                            SAVE
                                        </button>

                                        <div class="clearfix"></div>

                                      </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>

                            </div>
                        </div>
                    </div>

                    <div class="rg-kv">
                        <div class="rg-k">Feedback</div>
                        <div class="rg-v">
                            <div class="rg-note-actions">

                                <div style="font-size: 12px; padding-bottom: 10px; padding-top: 10px;">
                                    Add feedback about how the AI performed on this lead and what you would like to modify.
                                </div>

                                <v-expansion-panels v-model="panel2" accordion flat dense>
                                    <v-expansion-panel>
                                      <v-expansion-panel-header>Add Feedback</v-expansion-panel-header>
                                      <v-expansion-panel-content>
                                        <textarea v-model="activeLead.ai_feedback" style="padding: 10px; width: 100%; height: 200px; border: solid 1px #CCCCCC; border-radius: 5px;" class="mt-2"></textarea>

                                            <br />

                                            <v-switch color="#48bb78" inset dense hide-details :ripple="false" v-model="activeLead.ai_feedback_urgent" label="Urgent"></v-switch>

                                            <br />

                                            <button class="rg-dialog-button-save rounded text-white text-xs text-bold focus:outline-none py-2 px-4 w-full mb-2" :class="{'spinner': savingAiFeedback}" @click="saveAiFeedback(activeLead)">
                                                SAVE
                                            </button>

                                        <div class="clearfix"></div>

                                      </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>

                            </div>
                        </div>
                    </div>

                    <div class="rg-kv">
                        <div class="rg-k">Google LSA rating</div>
                        <div class="rg-v">
                            <span v-if="activeLead.rated_at" class="rg-metric">
                                <v-tooltip bottom content-class="intake-tooltip">
                                      <template v-slot:activator="{ on, attrs }">
                                        <v-chip
                                          v-bind="attrs"
                                          v-on="on"
                                          :color="getSatisfactionColor(convertSatisfactionToNumber(activeLead.satisfaction))"
                                          dark
                                          class="font-weight-black"
                                          style="height: 28px; padding: 0 10px; font-size: 13px;"
                                        >
                                          <span style="font-weight: 900; font-size: 13px;">
                                            {{ convertSatisfactionToNumber(activeLead.satisfaction) }}
                                          </span>
                                        </v-chip>
                                      </template>
                                      <span class="tooltip-text">
                                        Google LSA Rating Notes: {{ activeLead.additional_comments || activeLead.summary }}
                                      </span>
                                    </v-tooltip>
                                    <span class="score-label" style="margin-left:10px;">
                                      <strong>{{ satisfactionLabelByTexts(activeLead.satisfaction) }}</strong>
                                    </span>
                            </span>
                            <span v-else class="rg-muted">Not Rated</span>
                        </div>
                    </div>

                    <div class="rg-kv" v-if="me">
                        <div class="rg-k">Value</div>
                        <div class="rg-v">
                            <div :class="'lsa-show-value lsa-show-value-' + activeLead.id">
                                <a href="javascript: void(0);" @click="showEditValue(activeLead)">${{ activeLead.value }}<i class="mdi mdi-pencil-outline ml-2"></i></a>
                            </div>
                            <div :class="'lsa-edit-value lsa-edit-value-' + activeLead.id" style="display: none; position: relative; width: 140px;">
                                <input type="text" :value="((activeLead.value > 0) ? activeLead.value : me.settings.customer_value)" style="padding: 5px; border: solid 1px #CCCCCC; width: 100px;" />
                                <div style="position: absolute; margin-top: -22px; right: -31px;">
                                    <a href="javascript: void(0);" style="color: #3EBB80 !important;" @click="editValue(activeLead)"><i class="mdi mdi-check"></i></a>
                                    <a href="javascript: void(0);" style="color: #E53F71 !important;" @click="cancelEditValue(activeLead)"><i class="mdi mdi-close"></i></a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="rg-kv">
                        <div class="rg-k">Channels</div>
                        <div class="rg-v rg-channels-inline">
                            <span v-for="p in normalizedProviders(activeLead)" :key="p.key" class="rg-pill rg-pill--soft">{{ providerLabel(p.key) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Notes -->
                <div v-show="leadDialogTab==='notes'" class="rg-notes">
                    <div class="rg-note-block">
                        <div class="rg-k">Google LSA rating notes</div>
                        <div class="rg-note">{{ activeLead.additional_comments || activeLead.summary }}</div>
                    </div>

                    <div class="rg-note-block">
                        <div class="rg-k">Intake performance (IP) notes</div>
                        <div class="rg-note">{{ (activeLead.intake_performance && activeLead.intake_performance.explanation) ? activeLead.intake_performance.explanation : 'N/A' }}</div>
                    </div>

                    <div class="rg-note-block">
                        <div class="rg-k">AI performance (AIP) notes</div>
                        <div class="rg-note">{{ (activeLead.ai_performance && activeLead.ai_performance.explanation) ? activeLead.ai_performance.explanation : 'N/A' }}</div>
                    </div>
                </div>

                <!-- Transcripts -->
                <div v-show="leadDialogTab==='transcripts'" class="rg-transcripts">
                    <!-- <div class="rg-channel-tabs">
                        <button
                          v-for="p in normalizedProviders(activeLead)"
                          :key="p.key"
                          type="button"
                          class="rg-chip-tab"
                          :class="{active: activeLeadChannel===p.key}"
                          @click="activeLeadChannel = p.key"
                        >
                            {{ providerLabel(p.key) }}
                        </button>

                        <button
                          v-if="activeLead.sources && activeLead.sources.indexOf('web') != -1"
                          type="button"
                          class="rg-chip-tab"
                          :class="{active: activeLeadChannel==='web'}"
                          @click="activeLeadChannel='web'"
                        >
                            Web Chat
                        </button>
                    </div> -->

                    <div class="rg-transcript-actions">

                        <div v-if="transcriptsLoading" class="py-4">
                            <v-skeleton-loader type="article, article"></v-skeleton-loader>
                        </div>

                        <div v-else>

                          <!-- LSA -->
                          <div v-if="activeLead.sources.indexOf('lsa') != -1">
                              <div class="rg-k mb-2">LSA</div>

                              <div v-if="activeLead.lead_type === 'PHONE_CALL'">
                                  <div v-if="(transcriptData.lsa_recordings || []).length===0" class="rg-muted">No recordings found.</div>
                                  <div v-else class="rg-audio-list">
                                      <div v-for="(src, i) in transcriptData.lsa_recordings" :key="'lsa-audio-'+i" class="mb-4">
                                          <audio controls :src="src" style="width:100%;"></audio>
                                      </div>
                                  </div>

                                  <div v-if="(transcriptData.lsa_transcript || []).length===0" class="rg-muted">No transcription found.</div>
                                  <div v-else class="rg-transcript-thread">
                                      <div v-for="(msg, idx) in transcriptData.lsa_transcript" :key="'lsamsg-'+idx" class="rg-msg" :class="msg.sender==='human' ? 'is-human' : 'is-bot'">
                                          <div class="rg-msg__meta" v-show="msg.message || msg.text || msg.content">
                                              <strong>{{ (msg.sender==='human' || msg.role==='user') ? 'Customer' : 'Assistant' }}</strong>
                                              <span v-if="msg.timestamp || msg.start" class="rg-muted"> • {{ displayTimestamp(activeLead, msg.timestamp, activeLead.lead_creation_date, msg.start) }}</span>
                                          </div>
                                          <div class="rg-msg__text">{{ msg.message || msg.text || msg.content }}</div>

                                          <assistant-message-feedback
                                              v-if="msg.sender !== 'human' && msg.sender !== 'user' && msg.role !== 'user'"
                                              :message-text="msg.message || msg.text || msg.content"
                                              :phone-number="activeLead.phone_number || activeLead.ai_provided_phone_number"
                                              :transcript-context="transcriptData.lsa_transcript"
                                              :preceding-user-message="getPrecedingUserMessage(transcriptData.lsa_transcript, idx)"
                                          />
                                      </div>
                                  </div>
                              </div>

                              <div v-else>
                                  <div v-if="(transcriptData.lsa_messages || []).length===0" class="rg-muted">No messages found.</div>
                                  <div v-else class="rg-transcript-thread">
                                      <div v-for="(msg, idx) in transcriptData.lsa_messages" :key="'lsamsg-'+idx" class="rg-msg" :class="msg.sender==='human' ? 'is-human' : 'is-bot'">
                                          <div class="rg-msg__meta" v-show="msg.message || msg.text || msg.content">
                                              <strong>{{ (msg.sender==='human' || msg.role==='user') ? 'Customer' : 'Assistant' }}</strong>
                                              <span v-if="msg.timestamp || msg.start" class="rg-muted"> • {{ displayTimestamp(activeLead, msg.timestamp, activeLead.lead_creation_date, msg.start) }}</span>
                                          </div>
                                          <div class="rg-msg__text">{{ msg.message || msg.text || msg.content }}</div>

                                          <assistant-message-feedback
                                              v-if="msg.sender !== 'human' && msg.sender !== 'user' && msg.role !== 'user'"
                                              :message-text="msg.message || msg.text || msg.content"
                                              :phone-number="activeLead.phone_number || activeLead.ai_provided_phone_number"
                                              :transcript-context="transcriptData.lsa_messages"
                                              :preceding-user-message="getPrecedingUserMessage(transcriptData.lsa_messages, idx)"
                                          />
                                      </div>
                                  </div>
                              </div>
                          </div>

                          <!-- Web chat -->
                          <div v-else-if="activeLead.sources.indexOf('web') != -1">
                              <div class="rg-k mb-2">Web chat conversation</div>

                              <div v-if="!transcriptData.web || !transcriptData.web.messages || transcriptData.web.messages.length===0" class="rg-muted">
                                  No conversation found.
                              </div>

                              <div v-else class="rg-transcript-thread">
                                  <div v-for="(msg, idx) in transcriptData.web.messages" :key="'webmsg-'+idx" class="rg-msg" :class="msg.sender==='human' ? 'is-human' : 'is-bot'">
                                      <div class="rg-msg__meta">
                                          <strong>{{ (msg.sender==='human' || msg.role==='user') ? 'Customer' : 'Assistant' }}</strong>
                                          <span v-if="msg.timestamp || msg.start" class="rg-muted"> • {{ displayTimestamp(activeLead, msg.timestamp, activeLead.lead_creation_date, msg.start) }}</span>
                                      </div>
                                      <div class="rg-msg__text">{{ msg.message || msg.text || msg.content }}</div>

                                      <assistant-message-feedback
                                          v-if="msg.sender !== 'human' && msg.sender !== 'user' && msg.role !== 'user'"
                                          :message-text="msg.message || msg.text || msg.content"
                                          :phone-number="activeLead.phone_number || activeLead.ai_provided_phone_number"
                                          :transcript-context="transcriptData.web.messages"
                                          :preceding-user-message="getPrecedingUserMessage(transcriptData.web.messages, idx)"
                                      />
                                  </div>
                              </div>
                          </div>

                          <!-- AI / Bland transcript -->
                          <div v-else-if="activeLead.sources.indexOf('bland') != -1">
                              <div v-if="activeLead.bland_recording_url" class="mb-4">
                                  <div class="rg-k mb-1">Recording</div>
                                  <audio controls :src="activeLead.bland_recording_url" style="width:100%;"></audio>
                              </div>

                              <div class="rg-k mb-2">Transcript</div>

                              <div v-if="!transcriptData.bland || !transcriptData.bland.messages || transcriptData.bland.messages.length===0" class="rg-muted">
                                  No transcript found.
                              </div>

                              <div v-else class="rg-transcript-thread">
                                  <div v-for="(msg, idx) in transcriptData.bland.messages" :key="'bmsg-'+idx" class="rg-msg" :class="msg.sender==='human' ? 'is-human' : 'is-bot'">
                                      <div class="rg-msg__meta" v-show="msg.message || msg.text || msg.content">
                                          <strong>{{ (msg.sender==='human' || msg.sender==='user' || msg.role==='user') ? 'Customer' : 'Assistant' }}</strong>
                                          <span v-if="msg.timestamp || msg.start" class="rg-muted"> • {{ displayTimestamp(activeLead, msg.timestamp, activeLead.lead_creation_date, msg.start) }}</span>
                                      </div>
                                      <div class="rg-msg__text">{{ msg.message || msg.text || msg.content }}</div>

                                      <assistant-message-feedback
                                          v-if="msg.sender !== 'human' && msg.sender !== 'user' && msg.role !== 'user'"
                                          :message-text="msg.message || msg.text || msg.content"
                                          :phone-number="activeLead.phone_number || activeLead.ai_provided_phone_number"
                                          :transcript-context="transcriptData.bland.messages"
                                          :preceding-user-message="getPrecedingUserMessage(transcriptData.bland.messages, idx)"
                                      />
                                  </div>
                              </div>
                          </div>

                          <!-- CallRail transcript -->
                          <div v-else-if="activeLead.sources.indexOf('callrail') != -1">
                              <div v-if="activeLead.call_rail_recording_url" class="mb-4">
                                  <div class="rg-k mb-1">Recording</div>
                                  <audio controls :src="activeLead.call_rail_recording_url.replace('/recording', '/recording/redirect')" style="width:100%;"></audio>
                              </div>
                              <div class="rg-k mb-2">Transcript</div>
                              <div v-if="!transcriptData.bland || !transcriptData.bland.messages || transcriptData.bland.messages.length===0" class="rg-muted">
                                  No transcript found.
                              </div>
                          </div>

                          <!-- DialPad transcript -->
                          <div v-else-if="activeLead.sources.indexOf('dialpad') != -1">
                              <div v-if="activeLead.dialpad_recording_url" class="mb-4">
                                  <div class="rg-k mb-1">Recording</div>
                                  <audio controls :src="activeLead.dialpad_recording_url" style="width:100%;"></audio>
                              </div>
                              <div class="rg-k mb-2">Transcript</div>
                              <div v-if="!transcriptData.bland || !transcriptData.bland.messages || transcriptData.bland.messages.length===0" class="rg-muted">
                                  No transcript found.
                              </div>
                          </div>

                          <div v-else class="rg-muted">No transcript view configured for this channel.</div>
                      </div>
                    </div>

                </div>

                <div v-show="leadDialogTab==='photos'" class="rg-photos">
                    <!-- NO PHOTOS -->
                    <div v-if="photos.length == 0" style="font-size: 13px;">
                        No photos uploaded.
                    </div>

                    <!-- PHOTO GRID -->
                    <div v-else class="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div v-for="(photo, index) in photos" :key="index" style="position: relative;">
                            
                            <img :src="photo"
                                 style="width: 100%; height: 150px; object-fit: cover; border-radius: 6px;" />

                            <button @click="removePhoto(index)"
                                    style="position: absolute; top: 5px; right: 5px; background: red; color: white; border: none; border-radius: 50%; width: 25px; height: 25px;">
                                ✕
                            </button>

                        </div>
                    </div>

                    <!-- FILE INPUT -->
                    <div class="mt-4">
                        <input type="file"
                               accept="image/*"
                               @change="handlePhotoUpload"
                               multiple :disabled="uploading" />

                        <div v-if="uploading" class="pt-3">Uploading... please wait</div>
                    </div>
                </div>
            </div>

            <div class="rg-modal__footer" v-if="activeLead">
                <button class="rg-secondary" type="button" @click="closeLeadDialog">Close</button>
            </div>
        </div>
    </div>

  </div>
</template>

<script>
import { sourceFilterOptions, phoneSource } from '@/config/phoneSources';
import { formatLeadCreationDate } from '@/utils/leadTime'
/* eslint-disable no-console */
import { mapState } from 'vuex'
import ConvertedBadge from '@/components/common/ConvertedBadge.vue'
import ConversionStats from '@/components/common/ConversionStats.vue'
import GhlConversionSync from '@/components/common/GhlConversionSync.vue'
import $ from 'jquery'
import axios from 'axios'
import moment from 'moment'
import AssistantMessageFeedback from '@/components/common/AssistantMessageFeedback.vue'
import LeadSurveyInstructions from '@/components/common/LeadSurveyInstructions.vue'
import HighValueBadge from '@/components/common/HighValueBadge.vue'

export default {
  components: {
    ConversionStats,
    GhlConversionSync,
    ConvertedBadge,
    AssistantMessageFeedback,
    LeadSurveyInstructions,
    HighValueBadge
  },
  computed: {
    convertedStatusCount() {
      if (this.dashboard_data && this.dashboard_data.conversions_stats) return this.dashboard_data.conversions_stats.total_conversions;
      return (this.messages || []).filter(lead => lead.is_converted == 1).length;
    },
    ...mapState({
      me: state => state.me.me
    }),

    sourceFilterOptions() {
      return sourceFilterOptions;
    },

    // True when the selected source is LSA or no source filter is applied (default view)
    isLsaSource() {
      return !this.source_filter || this.source_filter === 'lsa';
    },

    // LSA-only series names — hidden when a non-LSA source is selected
    lsaOnlySeriesNames() {
      return ['Impressions', 'Absolute Top Impressions Rate', 'Clicks', 'Total Cost', 'CPL', 'Credited'];
    },

    legendItems() {
      var self = this;
      return this.lineChartMetricsSeries
        .filter(function(series) {
          // Hide LSA-only series from the legend when viewing non-LSA sources
          if (!self.isLsaSource && self.lsaOnlySeriesNames.indexOf(series.name) !== -1) {
            return false;
          }
          return true;
        })
        .map(function(series) {
          // Use original index from full seriesColors array for consistent colors
          var colorIdx = self.lineChartMetricsSeries.indexOf(series);
          return {
            name: series.name,
            color: self.seriesColors[colorIdx],
            disabled: self.activeSeriesNames.indexOf(series.name) === -1,
            description: series.description,
          };
        });
    },

    computedHealthSummary() {
      // Return explicit backend summary if available
      // if (this.healthSummary) return this.healthSummary;
      
      const score = Number(this.healthScoreValue);
      if (this.healthScoreValue === null || isNaN(score)) {
        return 'Account health status is currently updating. Check back shortly.';
      }

      if (score >= 90) {
        return 'Your account is operating at peak efficiency. Core performance indicators are strong and driving optimal visibility.';
      } else if (score >= 80) {
        return 'Account health is in good standing. Performance remains solid, with minor opportunities for incremental growth.';
      } else if (score >= 70) {
        return 'Account performance is fair. Targeted adjustments can help retain momentum.';
      } else {
        return 'Account health is at risk. Immediate intervention is recommended to address critical gaps and restore stability.';
      }
    },
    
    impactMetricsList() {
      const impact = (this.dashboard_data && this.dashboard_data.impact_stats) || {};
      const stats = (this.dashboard_data && this.dashboard_data.stats) || {};

      return [
        {
          label: 'Calls Answered & Notated',
          value: impact.lsaCalls || this.dashboard_data.total_calls || 0
        },
        {
          label: 'Messages Responded to',
          value: impact.lsaMessages || this.dashboard_data.total_message_leads || 0
        },
        {
          label: 'Website Chats Answered',
          value: impact.websiteChats || 0
        },
        {
          label: 'Surveys Submitted',
          value: impact.surveysSubmitted || 0
        },
        {
          label: 'Robocalls Screened',
          value: impact.spamCallsBlocked || stats.blocked_robo_calls || 0
        },
        {
          label: 'Booked Appointments',
          value: impact.bookedAppointments || 0
        },
        {
          label: 'Transferred Calls',
          value: impact.transferredCalls || 0
        },
        {
          label: 'Auto-Retried Messages',
          value: impact.autoRetriedMessages || 0
        }
      ];
    },

    // Shows top 3 metrics with values > 0
    topImpactMetrics() {
      return this.impactMetricsList.slice(0, 3);
    },
    token() {
      return this.$store && this.$store.state.auth ? this.$store.state.auth.jwt : null;
    },
    filteredLeads() {
      if (!this.messages) return [];

      return this.messages.filter(lead => {
        if (this.filter_calls_type && this.filter_calls_type === 'All Except Robocalls') {
          const tagString = JSON.stringify(lead.tag || '').toLowerCase();
            if (tagString.includes('spam')) return false;

          return true;
        }

        // 1. Dropdown Filter Logic
        if (this.filter_calls_type && this.filter_calls_type !== 'Show All') {
            if (this.filter_calls_type === 'Converted') return lead.is_converted == 1;
            if (this.filter_calls_type === 'In Contact') return lead.conversion_status === 'in_contact';
          
          // 1. AI High Value Filter
          if (this.filter_calls_type === 'High Value' || this.filter_calls_type === 'High Value') {
            // Primary check: AI evaluation flag from backend
            if (lead.is_high_value === true || lead.is_high_value === 1) {
              return true;
            }

            return false;
          }

          // Disputed (PENDING or APPROVED)
          if (this.filter_calls_type === 'Disputed') {
            if (lead.satisfaction == "DISSATISFIED" || lead.satisfaction == "VERY_DISSATISFIED") return true;
            const status = lead.dispute_status ? lead.dispute_status.toUpperCase() : '';
            if (status !== 'PENDING' && status !== 'APPROVED') return false;
          }

          // Missed Calls
          if (this.filter_calls_type === 'Missed Calls') {
            const isPhoneCall = lead.lead_type === 'PHONE_CALL';
            const tagString = JSON.stringify(lead.tag || '').toLowerCase();

            if (tagString.includes('spam')) return false;
            
            // If it's NOT a phone call
            if (!isPhoneCall) {
              return false;
            }

            if (this.isDroppedCall(lead)) {
              return true;
            }

            // If it doesn't have the "missed" tag, filter it out
            if (!tagString.includes('missed')) {
              return false;
            }
          }

          // Robocalls
          if (this.filter_calls_type === 'Robocalls') {
            const tagString = JSON.stringify(lead.tag || '').toLowerCase();
            if (!tagString.includes('spam')) return false;
          }

          // Organic
          if (this.filter_calls_type === 'Organic') {
            const sources = lead.sources || [];

            // Organic is currently anything that isn't explicitly attributed
            // to LSA, AI, Web Chat, etc.
            if (
              sources.includes('lsa')
            ) {
              return false;
            }

            return true;
          }

          // AI Leads
          if (this.filter_calls_type === 'AI Leads') {
            return (lead.sources || []).includes('bland');
          }

          // Web Chat Leads
          if (this.filter_calls_type === 'Web Chat Leads') {
            return (lead.sources || []).includes('web');
          }

          // LSA Leads
          if (this.filter_calls_type === 'LSA Leads') {
            return (lead.sources || []).includes('lsa');
          }
        }

        // 2. Lead Quality Filter
        if (this.lead_quality_filter) {
          const qualityMap = {
            5: "VERY_SATISFIED",
            4: "SATISFIED",
            3: "NEUTRAL",
            2: "DISSATISFIED",
            1: "VERY_DISSATISFIED"
          };
          if (lead.satisfaction !== qualityMap[this.lead_quality_filter]) {
            return false;
          }
        }

        // 3. Agent Performance Filter
        if (this.performance_filter) {
          const perfVal = String(this.performance_filter);
          const aiRating = (lead.ai_performance && lead.ai_performance.rating) ? String(lead.ai_performance.rating) : null;
          const intakeRating = (lead.intake_performance && lead.intake_performance.rating) ? String(lead.intake_performance.rating) : null;
          if (aiRating !== perfVal && intakeRating !== perfVal) {
            return false;
          }
        }

        return true;
      });
    },
    calculatedWeeklyAvgLeads() {
        const total = this.dashboard_data ? (this.dashboard_data.total_leads || 0) : 0;
        return (total / 4).toFixed(1);
    },
    headers() {
      return [
        { text: 'Customer', value: 'customer_name', width: 200, show: true },
        { text: 'Channels', value: 'sources', show: true },
        { text: 'Lead Received', value: 'lead_creation_date', show: true },
        { text: 'Charge Status', value: 'charge_status', width: 170, show: true },
        { text: 'Google LSA Rating', value: 'ai_rating', align: 'start', sortable: false, width: 200, show: true },
        { text: 'Google LSA Rating Notes', value: 'lead_quality_notes', align: 'start', width: 200, sortable: false, show: true },
        { text: 'IP', value: 'intake_performance', align: 'start', sortable: false, show: true },
        { text: 'Intake Performance (IP) Notes', value: 'intake_performance_notes', align: 'start', sortable: false, width: 220, show: true },
        { text: 'AIP', value: 'ai_performance', align: 'start', sortable: false, show: true },
        { text: 'AI Performance (AIP) Notes', value: 'ai_performance_notes', align: 'start', sortable: false, width: 200, show: true },
        { text: 'Tags', value: 'tag', width: 130, show: this.me },
        { text: 'Value', value: 'value', width: 100, show: this.me },
        { text: 'Actions', value: 'id', width: 100, show: this.me }
      ].filter(h => h.show)
    },

    /* DYNAMIC METRIC CALCULATIONS */
    answeredUnder30sRate() {
      if (this.dashboard_data && this.dashboard_data.stats && typeof this.dashboard_data.stats.lsa_30_seconds_response_rate !== 'undefined') {
        return parseFloat(this.dashboard_data.stats.lsa_30_seconds_response_rate).toFixed(0);
      }
      return '84';
    },
    previousAnsweredUnder30sRate() {
      if (this.dashboard_data_prev && this.dashboard_data_prev.stats && typeof this.dashboard_data_prev.stats.lsa_30_seconds_response_rate !== 'undefined') {
        return parseFloat(this.dashboard_data_prev.stats.lsa_30_seconds_response_rate).toFixed(0);
      }
      return '78';
    },
    answeredUnder30sCallsCount() {
      const total = this.dashboard_data ? (this.dashboard_data.total_calls || 0) : 0;
      const rate = parseFloat(this.answeredUnder30sRate) / 100;
      return Math.round(total * rate);
    },
    totalFollowupsSent() {
      if (this.dashboard_data && typeof this.dashboard_data.review_requests_sent !== 'undefined') {
        return (this.dashboard_data.review_requests_sent || 0) + (this.stats.followUpsSent || 0);
      }
      return 64;
    },
    surveysSubmittedCount() {
      if (this.dashboard_data && this.dashboard_data.stats && typeof this.dashboard_data.stats.surveys_submitted !== 'undefined') {
        return this.dashboard_data.stats.surveys_submitted;
      }
      return this.stats.surveysSubmitted || 9;
    },
    surveyPercentage() {
      const total = this.dashboard_data.total_leads || 0;
      if (total === 0) return 0;
      return Math.round((this.surveysSubmittedCount / total) * 100);
    },

    /* HERO RING COMPUTATIONS (DERIVED DYNAMICALLY FROM FETCHHEALTH) */
    healthScoreValue() {
      if (this.healthScore !== null && typeof this.healthScore !== 'undefined') {
        return this.healthScore;
      }
      if (this.dashboard_data && typeof this.dashboard_data.health_score === 'number') {
        return this.dashboard_data.health_score;
      }
      return 87;
    },
    healthScoreLabel() {
      if (this.healthLabel) return this.healthLabel.toUpperCase();
      const s = this.healthScoreValue;
      if (s >= 90) return 'EXCELLENT';
      if (s >= 80) return 'GOOD';
      if (s >= 70) return 'FAIR';
      return 'RISK';
    },
    heroRingStyle() {
      const score = this.healthScoreValue;
      let color = '#c9a227';
      if (score >= 90) color = '#1d7a46';
      else if (score >= 80) color = '#c9a227';
      else if (score >= 70) color = '#d07c2e';
      else color = '#c0392b';
      return {
        background: `conic-gradient(${color} 0% ${score}%, #e3e9ee ${score}% 100%)`
      };
    },
    // Extract first and last month labels dynamically from lineChartMetricsOptions
      chartStartMonthLabel() {
        const cats = this.lineChartMetricsOptions.xaxis.categories || [];
        return cats.length > 0 ? cats[0] : 'Feb';
      },
      chartEndMonthLabel() {
        const cats = this.lineChartMetricsOptions.xaxis.categories || [];
        return cats.length > 0 ? cats[cats.length - 1] : 'Jul';
      },

      // Dynamic series arrays
      leadVolumeData() {
        return this.getSeriesDataByName('Lead Volume');
      },
      under30sData() {
        return this.getSeriesDataByName('Under 30s Response Rate');
      },
      leadQualityData() {
        return this.getSeriesDataByName('Lead Quality');
      }
  },
  data() {
    var self = this;
    return {
      messages: [],
      loading: true,
      loadingHealth: true,
      loadingMessages: true,
      loadError: '',

      /* /me/lsa-health DYNAMIC DATA FIELDS */
      businessName: '',
      period: '',
      healthScore: null,
      healthLabel: '',
      healthSummary: '',
      hoursSaved: '',
      whatWeDid: [],
      setUpNext: [],

      defaultTasks: [
        {
          tagClass: 'urgent',
          tagLabel: 'URGENT',
          title: 'Review Unhandled Missed Leads',
          description: 'Instantly filter leads requiring manual response or client callbacks.',
          actionType: 'filter',
          action_label: 'Filter Missed'
        },
        {
          tagClass: 'disputes',
          tagLabel: 'DISPUTES',
          title: 'Automated Disputes Log',
          description: 'AI files disputes with transcript evidence. Review approved credits.',
          actionType: 'disputed',
          action_label: 'See disputed leads'
        },
        {
          tagClass: 'reviews',
          tagLabel: 'REPORTS',
          title: 'Monthly Performance Report',
          description: 'Access live client reports in browser or trigger automated email delivery.',
          actionType: 'reports'
        }
      ],

      selected_frequency: 'Frequency: Auto',
      selected_frequency_chart: 'Monthly',
      show_picker: false,
      date_filter: [
        moment().subtract(30, 'days').format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD')
      ],

      stats: {
        websiteChats: 0,
        lsaCalls: 0,
        lsaMessages: 0,
        followUpsSent: 0,
        surveysSubmitted: 0,
        spamCallsBlocked: 0,
        transferredCalls: 0,
        bookedAppointments: 0,
        hoursSaved: 0
      },

      all_time: 0,
      selected_date_text: "Last 30 days",
      filter_only_lsa: 0,

      new_note: "",
      show_notes_dialog: false,
      notes_lead: null,
      savingNote: false,

      cutoffDate: null,
      selected: [],
      company_name: "",
      uuid: "",
      ai_started_on: "",
      survey_started_on: "",
      first_lead_on: "",

      selectedMonth: '',
      months: [],
      startDate: '',
      endDate: '',

      dashboard_data: {},
      dashboard_data_prev: {},
      comparisons: {},

      custom_tags: [],
      custom_statuses: [],

      aiSuggestionById: {},
      aiLoadingById: {},
      aiLoadingInakePerformanceById: {},
      aiLoadingAiPerformanceById: {},
      aiLoadingLeadQualityPerformanceById: {},

      tags_items: [
        { index: 0, text: 'Not Set' },
        { index: 1, text: 'Existing Customer' },
        { index: 2, text: 'Missed Call' },
        { index: 3, text: 'Opportunity' },
        { index: 4, text: 'Meeting Scheduled' },
        { index: 5, text: 'Became Customer' },
        { index: 'Signed', text: 'Signed' },
        { index: 'Booked for Consultation', text: 'Booked for Consultation' },
        { index: 'Declined/Not a lead', text: 'Declined/Not a lead' },
        { index: 'Wrong Number', text: 'Wrong Number' },
        { index: 'No Response', text: 'No Response' },
        { index: 'Existing Client', text: 'Existing Client' },
        { index: 'Failed Sign Up', text: 'Failed Sign Up' },
        { index: 'Following Up/Working on this one', text: 'Following Up/Working on this one' },
        { index: 'E-Doc sent not signed', text: 'E-Doc sent not signed' },
        { index: 'Booked to Service', text: 'Booked to Service' },
        { index: 'Booked for Appointment', text: 'Booked for Appointment' },
        { index: 'Cancelled', text: 'Cancelled' },
        { index: 'Complete', text: 'Complete' },
        { index: 'Incomplete', text: 'Incomplete' },
        { index: 'AI Test Call', text: 'AI Test Call' },
        { index: 'Duplicate Call', text: 'Duplicate Call' },
        { index: 'Assisted Signing', text: 'Assisted Signing' }
      ],

      // Static series colors — never mutated (safe from updateOptions side-effects)
      metricsChartReady: false,
      seriesColors: ["#16a34a", "rgb(255, 69, 96)", "rgb(0, 227, 150)", "rgb(0, 143, 251)", "rgb(254, 176, 25)", "#EC4899", "rgb(119, 93, 208)", "#4284C1", "rgb(56, 193, 167)", "#F97316", "#7c3aed", "#0891b2", "#15803d", "#be185d"],

      lineChartMetricsOptions: {
              colors: ["#16a34a", "rgb(255, 69, 96)", "rgb(0, 227, 150)", "rgb(0, 143, 251)", "rgb(254, 176, 25)", "#EC4899", "rgb(119, 93, 208)", "#4284C1", "rgb(56, 193, 167)", "#F97316"],
              chart: {
                  id: "line-chart",
                  toolbar: {
                      show: false
                  },
                  events: {}
              },
              legend: {
                  show: false
                },
              xaxis: {
                  categories: [
                      "Jan",
                      "Feb",
                      "Mar",
                      "Apr",
                      "May",
                      "Jun",
                      "Jul",
                      "Aug",
                      "Sep",
                      "Oct",
                      "Nov",
                      "Dec",
                  ],
                  labels: {
                    style: {
                      colors: "#757B89"
                    }
                  }
              },
              yaxis: [
                {
                  seriesName: "Under 30s Response Rate",
                  show: false,
                  opposite: false,
                  min: 0,
                  max: 100,
                  labels: {
                    show: false
                  }
                },
                {
                  seriesName: "Lead Volume",
                  show: false,
                  opposite: false,
                  min: 0,
                  labels: {
                    show: false
                  }
                },
                {
                  seriesName: "Lead Quality",
                  show: false,
                  opposite: false,
                  min: 0,
                  max: 5,
                  labels: {
                    show: false
                  }
                },
                {
                  seriesName: "Impressions",
                  show: false,
                  opposite: false,
                  min: 0,
                  labels: {
                    show: false
                  }
                },
                {
                  seriesName: "Absolute Top Impressions Rate",
                  show: false,
                  opposite: false,
                  min: 0,
                  max: 100,
                  labels: {
                    show: false
                  }
                },
                {
                  seriesName: "Reviews",
                  show: false,
                  opposite: false,
                  min: 0,
                  labels: {
                    show: false
                  }
                },
                {
                  seriesName: "Clicks",
                  show: false,
                  opposite: false,
                  min: 0,
                  labels: {
                    show: false
                  }
                },
                {
                  seriesName: "Total Cost",
                  show: false,
                  opposite: false,
                  min: 0,
                  labels: {
                    show: false
                  }
                },
                {
                  seriesName: "Credited",
                  show: false,
                  opposite: false,
                  min: 0,
                  labels: {
                    show: false
                  }
                },
                {
                  seriesName: "CPL",
                  show: false,
                  opposite: false,
                  min: 0,
                  labels: {
                    show: false
                  }
                }
              ],
              stroke: {
                  curve: "smooth",
                  width: 4
              },
              grid: {
                borderColor: '#0000000d',
                show: true,
                xaxis: {
                  lines: {
                    show: false
                  }
                },
                yaxis: {
                  lines: {
                    show: true,
                    color: '#FF0000'
                  }
                },
                padding: {
                  left: 35
                }
              },

              tooltip: {
                shared: true,
                intersect: false,
                custom: function(opts) {
                  var series = opts.series;
                  var dataPointIndex = opts.dataPointIndex;
                  var w = opts.w;

                  // Determine which series are currently visible
                  var visibleNames = self.hoveredLegend
                    ? [self.hoveredLegend]
                    : self.activeSeriesNames;

                  // Value formatters matching the original per-series formatters
                  var formatters = [
                    function(y) { return y != null ? y.toFixed(2) + '%' : ''; },
                    function(y) { return y != null ? String(parseInt(y)) : ''; },
                    function(y) { return y != null ? parseFloat(y).toFixed(2) : ''; },
                    function(y) { return y != null ? String(parseInt(y)) : ''; },
                    function(y) { return y != null ? y.toFixed(2) + '%' : ''; },
                    function(y) { return y != null ? String(parseInt(y)) : ''; },
                    function(y) { return y != null ? String(parseInt(y)) : ''; },
                    function(y) { return y != null ? '$' + y : ''; },
                    function(y) { return y != null ? String(parseInt(y)) : ''; },
                    function(y) { return y != null ? '$' + y : ''; }
                  ];

                  var xLabel = w.globals.categoryLabels[dataPointIndex]
                    || (w.globals.labels && w.globals.labels[dataPointIndex])
                    || '';

                  var html = '<div class="apexcharts-tooltip-title" style="font-weight:600;padding:6px 10px;border-bottom:1px solid #e0e0e0">' + xLabel + '</div>';

                  for (var i = 0; i < series.length; i++) {
                    var name = w.globals.seriesNames[i];
                    if (visibleNames.indexOf(name) === -1) continue;

                    var val = series[i][dataPointIndex];
                    var fmt = (name === 'Conversion Rate' || name === 'Ad-spend ROI') ? (y => y == null ? 'Unavailable' : Number(y).toFixed(2) + '%') : name === 'Collected Revenue' ? (y => y == null ? 'Unavailable' : Number(y).toFixed(2)) : formatters[i] || function(y) { return y != null ? String(y) : ''; };
                    var color = self.seriesColors[i] || '#000';

                    html += '<div class="apexcharts-tooltip-series-group apexcharts-active" style="display:flex;align-items:center;padding:4px 10px">';
                    html += '<span class="apexcharts-tooltip-marker" style="background-color:' + color + ';width:10px;height:10px;border-radius:50%;display:inline-block;margin-right:6px"></span>';
                    html += '<span class="apexcharts-tooltip-text"><span class="apexcharts-tooltip-y-label">' + name + ': </span><span class="apexcharts-tooltip-y-value"><strong>' + fmt(val) + '</strong></span></span>';
                    html += '</div>';
                  }

                  return html;
                }
              }
          },
          lineChartMetricsSeries: [
              {
                name: "Reviews",
                data: [30, 40, 25, 50, 49, 49],
              },
          ],

      legend: [
        { value: 5, label: "Very good", class: "score-1" },
        { value: 4, label: "Good", class: "score-2" },
        { value: 3, label: "Neutral", class: "score-3" },
        { value: 2, label: "Bad", class: "score-4" },
        { value: 1, label: "Very bad", class: "score-5" }
      ],

      panel: 0,
      panel2: 0,
      hoveredLegend: null,
      // Names of series currently active (visible) on the chart — toggled by click
      activeSeriesNames: ['Under 30s Response Rate', 'Lead Volume', 'Lead Quality'],
      
      photos: [],
      savingPhotos: false,
      lead_id: null,

      leadDialogOpen: false,
      activeLead: null,
      leadDialogTab: 'overview',
      activeLeadChannel: null,

      transcriptsLoading: false,
      transcriptCache: {},
      transcriptData: {
        bland: null,
        lsa_recordings: [],
        lsa_messages: [],
        web: null
      },

      uploading: false,
      showFullactiveLeadKey: 0,
      showExtraFilters: false,
      lead_quality_filter: null,
      performance_filter: null,
      surveyed_leads: null,

      ratingMenu: {},
      selectedRatings: {},
      selectedRatingNotes: {},
      savingRating: {},

      expanded_chart: false,

      showImpactBreakdown: false, // Controls hover/click dropdown state

      filter_calls_type: 'All Except Robocalls',
      source_filter: null,

      savingAiFeedback: false,
      ai_feedback: "",
      ai_feedback_urgent: false
    }
  },
  methods: {
    getPrecedingUserMessage(messages, currentIndex) {
      // Loop backward from the current assistant message index
      for (let i = currentIndex - 1; i >= 0; i--) {
        const msg = messages[i];
        const isUser = msg.sender === 'human' || msg.sender === 'user' || msg.role === 'user';
        if (isUser) {
          return msg.message || msg.text || msg.content || '';
        }
      }
      return ''; // Fallback if no prior user message is found
    },
    getSeriesMinMax(series) {
      if (!series) return { min: 0, max: 100 };

      const name = series.name;
      if (name === 'Under 30s Response Rate' || name === 'Absolute Top Impressions Rate') {
        return { min: 0, max: 100 };
      }
      if (name === 'Lead Quality') {
        return { min: 0, max: 5 };
      }

      // Define which series names represent money and need to be divided by 1 million
      // Add your actual financial series names to this array:
      const financialSeriesNames = ['Cost']; 
      const shouldDivideByMillion = financialSeriesNames.includes(name);

      const values = (series.data || [])
        .filter(value => value !== null && value !== undefined)
        .map(value => {
          // Remove commas from string numbers (e.g., '64,004,166.67' -> '64004166.67')
          const cleanVal = typeof value === 'string' ? value.replace(/,/g, '') : value;
          const num = Number(cleanVal);
          
          if (isNaN(num)) return null;

          // Only divide by 1 million if it's a financial series
          return shouldDivideByMillion ? num / 1000000 : num;
        })
        .filter(value => value !== null && !isNaN(value));

      if (!values.length) {
        return {
          min: 0,
          max: 1
        };
      }

      const maxVal = Math.max(...values);
      return {
        min: 0,
        max: maxVal > 0 ? maxVal : 1
      };
    },
    getYAxisFormatter(name) {
      switch (name) {
        case 'Conversion Rate':
        case 'Ad-spend ROI':
        case 'Under 30s Response Rate':
          return value => value != null && !isNaN(value) ? `${Number(value).toFixed(2)}%` : '';

        case 'Total Cost':
        case 'CPL':
          return value => value != null && !isNaN(value) ? `$${Number(value).toFixed(2)}` : '';

        case 'Collected Revenue':
        case 'Lead Quality':
          return value => value != null && !isNaN(value) ? Number(value).toFixed(2) : '';

        case 'Conversions':
        case 'Lead Volume':
        case 'Impressions':
        case 'Reviews':
        case 'Clicks':
        case 'Credited':
          return value => value != null && !isNaN(value) ? Math.round(Number(value)) : '';

        case 'Absolute Top Impressions Rate':
          return value => value != null && !isNaN(value) ? `${Number(value).toFixed(2)}%` : '';

        default:
          return value => value != null ? value : '';
      }
    },
    isDroppedCall(lead) {
      if (lead.lead_type != "PHONE_CALL")
        return false;

      if (lead.tag.includes('spam'))
        return false;

      if (lead.is_high_value == 1)
        return false;

      if (lead.lsa_transcription) {
        let tr = JSON.parse(lead.lsa_transcription);
        if (tr.length > 4)
          return false;
      }

      if (lead.bland_transcription) {
        let tr = JSON.parse(lead.bland_transcription);
        if (tr.length > 4)
          return false;
      }

      return true;
    },
    async onFilterChange() {
      await this.getMessages(true);
    },

    // Method called when selecting an item in the dropdown
    filterCallsType(val) {
      this.filter_calls_type = val || 'All Except Robocalls';
      this.onFilterChange();
    },

    // Button 1: "Filter Missed" & "Filter Pending Leads"
    filterMissedCalls() {
      this.filter_calls_type = 'Missed Calls';
      this.onFilterChange();
      this.scrollToRecentLeads();
    },

    // Button 2: "See Disputed Leads"
    filterDisputedLeads() {
      this.filter_calls_type = 'Disputed';
      this.onFilterChange();
      this.scrollToRecentLeads();
    },

    scrollToRecentLeads() {
      this.$nextTick(() => {
        const section = document.querySelector('.rg-lead-list') || document.querySelector('#recent-leads-section');
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },

    // Main filter function (if filtering client-side in computed/methods)
    applyFilters() {
      this.scrollToRecentLeads();
    },

    getCustomTagColor(tag) {
        for (var i in this.custom_tags) {
            if (this.custom_tags[i].tag == tag)
                return this.custom_tags[i].color;
        }

        return "";  
    },

    async loadCustomTags() {
        let response = await axios.get(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/load-custom-tags`, {
            headers: {
                'Authorization': 'Bearer ' + this.$store.state.auth.jwt
            }
        });

        this.custom_tags = response.data.custom_tags;

        if (this.isOnlyAi()) {
            this.tags_items = [{
                    index: 0,
                    text: 'Not Set'
                },{
                    index: 1,
                    text: 'Existing Customer'
                },{
                    index: 2,
                    text: 'Missed Call'
                },{
                    index: 'Cancelled',
                    text: 'Cancelled'
                }, 
                {
                    index: 'Complete',
                    text: 'Complete'
                }, {
                    index: 'Incomplete',
                    text: 'Incomplete'
                }, {
                    index: 'AI Test Call',
                    text: 'AI Test Call'
                },
            ]
        }
        else {
            this.tags_items = [{
                    index: 0,
                    text: 'Not Set'
                },{
                    index: 1,
                    text: 'Existing Customer'
                },{
                    index: 2,
                    text: 'Missed Call'
                },{
                    index: 3,
                    text: 'Opportunity'
                },{
                    index: 4,
                    text: 'Meeting Scheduled'
                },{
                    index: 5,
                    text: 'Became Customer'
                },{
                    index: 'Signed',
                    text: 'Signed'
                }
                ,{
                    index: 'Booked for Consultation',
                    text: 'Booked for Consultation'
                }
                ,{
                    index: 'Declined/Not a lead',
                    text: 'Declined/Not a lead'
                }
                ,{
                    index: 'Wrong Number',
                    text: 'Wrong Number'
                }
                ,{
                    index: 'Auto Credited',
                    text: 'Auto Credited'
                }
                ,{
                    index: 'No Response',
                    text: 'No Response'
                }
                ,{
                    index: 'Existing Client',
                    text: 'Existing Client'
                }
                ,{
                    index: 'Failed Sign Up',
                    text: 'Failed Sign Up'
                }
                ,{
                    index: 'Following Up/Working on this one',
                    text: 'Following Up/Working on this one'
                }
                ,{
                    index: 'E-Doc sent not signed',
                    text: 'E-Doc sent not signed'
                }
                ,{
                    index: 'Booked to Service',
                    text: 'Booked to Service'
                }
                ,{
                    index: 'Booked for Appointment',
                    text: 'Booked for Appointment'
                }
                ,{
                    index: 'Cancelled',
                    text: 'Cancelled'
                }, 
                {
                    index: 'Complete',
                    text: 'Complete'
                }, {
                    index: 'Incomplete',
                    text: 'Incomplete'
                }, {
                    index: 'AI Test Call',
                    text: 'AI Test Call'
                }, {
                    index: 'Duplicate Call',
                    text: 'Duplicate Call'
                },
            ]
        }

        for (var i in this.custom_tags) {
            this.tags_items.push({
                index: this.custom_tags[i].tag,
                text: this.custom_tags[i].tag
            });
        }
    },

    getCustomStatusColor(status) {
        for (var i in this.custom_statuses) {
            if (this.custom_statuses[i].status == status)
                return this.custom_statuses[i].color;
        }

        return "";  
    },

    checkUrlHashForLead() {
      if (!window.location.hash) return;

      // Extract key-value pairs from hash (e.g. #month=2026-07&leadId=400927)
      const hash = window.location.hash.substring(1); // remove '#'
      const params = new URLSearchParams(hash);
      const leadId = params.get('leadId');

      if (leadId && this.messages && this.messages.length > 0) {
        // Find matching lead in messages array (comparing as string/number safely)
        const targetLead = this.messages.find(item => String(item.id) === String(leadId) || String(item.lead_id) === String(leadId));

        if (targetLead) {
          // Open modal
          this.openLeadDialog(targetLead);

          // Optional: Smooth scroll to the lead card if visible
          this.$nextTick(() => {
            const leadEl = document.querySelector(`[data-lead-id="${targetLead.id}"]`);
            if (leadEl) {
              leadEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          });
        }
      }
    },

    // Map an array of numerical values dynamically to SVG coordinate points
    buildSparklinePoints(dataArray, minVal = null, maxVal = null) {
      // Ensure array exists and convert any string numbers to floats
      if (!dataArray || !Array.isArray(dataArray) || dataArray.length === 0) {
        return '10,75 285,75';
      }

      const cleanNums = dataArray.map(v => parseFloat(v) || 0);

      const svgWidth = 275;  // 10 to 285 X-range
      const startX = 10;
      const minY = 20;       // Top padding (Y-min)
      const maxY = 75;       // Bottom line baseline Y

      // Auto-scale relative to data if min/max aren't explicitly passed
      let actualMin = minVal !== null ? minVal : Math.min(...cleanNums);
      let actualMax = maxVal !== null ? maxVal : Math.max(...cleanNums);

      // If all numbers in the array are equal, prevent division by 0 (flat line guard)
      if (actualMax === actualMin) {
        actualMax = actualMin + 1;
      }

      const stepX = cleanNums.length > 1 ? svgWidth / (cleanNums.length - 1) : svgWidth;

      return cleanNums.map((val, idx) => {
        const x = startX + (idx * stepX);
        const normalized = (val - actualMin) / (actualMax - actualMin);
        // Invert Y coordinate because SVG Y starts at top (0)
        const y = maxY - (normalized * (maxY - minY));
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      }).join(' ');
    },

    // Get the final endpoint dot coordinates (X, Y) for placing the circle at the end of the line
    getSparklineEndpoint(dataArray, minVal = null, maxVal = null) {
      const pointsStr = this.buildSparklinePoints(dataArray, minVal, maxVal);
      const points = pointsStr.split(' ');
      const lastPoint = points[points.length - 1].split(',');
      return {
        x: parseFloat(lastPoint[0]) || 285,
        y: parseFloat(lastPoint[1]) || 75
      };
    },

    // Calculate the dynamic X pixel offset for the vertical "AI Answering On" line based on ai_started_on month
    getAiStartedXPosition() {
      const categories = this.lineChartMetricsOptions.xaxis.categories || [];
      if (!this.ai_started_on || categories.length === 0) return 210;

      const aiMonthLabel = moment(this.ai_started_on).format('MMM');
      const index = categories.findIndex(c => c && c.includes(aiMonthLabel));

      if (index === -1) return 210;

      const svgWidth = 275;
      const startX = 10;
      const stepX = categories.length > 1 ? svgWidth / (categories.length - 1) : 0;
      return (startX + (index * stepX)).toFixed(1);
    },

    // Safely retrieve dynamic series data array by name from lineChartMetricsSeries
    getSeriesDataByName(seriesName) {
      if (!this.lineChartMetricsSeries || !Array.isArray(this.lineChartMetricsSeries)) return [];
      const found = this.lineChartMetricsSeries.find(s => s && s.name === seriesName);
      return found && Array.isArray(found.data) ? found.data : [];
    },
    getSatisfactionScoreClass(satisfaction) {
      const num = this.convertSatisfactionToNumber(satisfaction);
      const classMap = {
        5: 'score-1', // Very good
        4: 'score-2', // Good
        3: 'score-3', // Neutral
        2: 'score-4', // Bad
        1: 'score-5'  // Very bad
      };
      return classMap[num] || 'score-3';
    },
    getPerformanceScoreClass(rating) {
      const num = parseInt(rating, 10);
      const classMap = {
        5: 'score-1', // 5 -> #16a34a (Very Good - Green)
        4: 'score-2', // 4 -> #22c55e (Good - Light Green)
        3: 'score-3', // 3 -> #facc15 (Neutral - Yellow)
        2: 'score-4', // 2 -> #fb923c (Bad - Orange)
        1: 'score-5'  // 1 -> #ef4444 (Very Bad - Red)
      };
      return classMap[num] || 'info';
    },

    /* REQUEST /me/lsa-health ENDPOINT FOR DYNAMIC HEALTH HERO DATA */
    async fetchHealth() {
      if (!this.token) {
        this.loadError = 'You are signed out. Log in to view your results.';
        return;
      }
      this.loadingHealth = true;
      this.loadError = '';
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_ENDPOINT}/me/lsa-health`, {
          headers: {
            Authorization: `Bearer ${this.token}`,
            'RG-Auth-Loc-Ua': this.token
          }
        });
        const d = res.data || {};
        this.businessName = d.business_name || '';
        this.period = d.period || '';
        this.healthScore = typeof d.health_score === 'number' ? d.health_score : (parseInt(d.health_score, 10) || null);
        this.healthLabel = d.health_label || '';
        this.healthSummary = d.health_summary || '';
        this.hoursSaved = d.hours_saved || '';
        this.whatWeDid = Array.isArray(d.what_we_did) ? d.what_we_did : [];
        this.setUpNext = Array.isArray(d.set_up_next) ? d.set_up_next : [];
      } catch (e) {
        this.loadError = 'We could not load your results right now. Please refresh in a moment.';
      } finally {
        this.loadingHealth = false;
      }
    },

    getTrendBadgeClass(change) {
      if (!change || change == 0) return 'flat';
      return change > 0 ? 'up' : 'down';
    },
    openRatingMenu(item) {
      this.$set(this.selectedRatings, item.id, item.quality_rating || 0);
      this.$set(this.selectedRatingNotes, item.id, item.quality_note || '');
      this.$set(this.ratingMenu, item.id, true);
    },
    async submitRating(item) {
      const rating = this.selectedRatings[item.id];
      const note = this.selectedRatingNotes[item.id] || '';

      if (!rating) {
        this.$swal({ title: "Select Rating", text: "Please select a star rating between 1 and 5.", icon: "warning" });
        return;
      }

      this.$set(this.savingRating, item.id, true);
      try {
        await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/set-lead-quality-rating`, {
          lead_id: item.id,
          rating: rating,
          note: note
        }, {
          headers: { 'Authorization': 'Bearer ' + this.$store.state.auth.jwt }
        });

        item.quality_rating = rating;
        item.quality_note = note;

        if (rating === 5) {
          if (!Array.isArray(item.tag)) item.tag = [];
          if (!item.tag.includes('High Quality Lead')) item.tag.push('High Quality Lead');
        }

        this.$set(this.ratingMenu, item.id, false);
        this.getMessages(true);
      } catch (error) {
        console.error("Error saving lead rating:", error);
      } finally {
        this.$set(this.savingRating, item.id, false);
      }
    },
    onConversionSaved(data) {
      if (this.activeLead && this.activeLead.id == data.lead_id) this.$set(this.activeLead, 'is_converted', data.is_converted);
      this.messages.filter(lead => lead.id == data.lead_id).forEach(lead => this.$set(lead, 'is_converted', data.is_converted));
      [this.activeLead, ...this.messages].filter(lead => lead && lead.id == data.lead_id).forEach(lead => this.$set(lead, 'conversion_status', data.conversion_status));
      this.loadDashboard();
      this.loadDashboard2();
    },
    onSurveyInstructionsSaved(data) {
      const value = data.is_high_value ? 1 : 0;
      if (this.activeLead && this.activeLead.id == data.lead_id) this.$set(this.activeLead, 'is_high_value', value);
      this.messages.filter(lead => lead.id == data.lead_id).forEach(lead => this.$set(lead, 'is_high_value', value));
      if (data.rules_saved && this.me && this.me.settings) {
        this.$set(this.me.settings, 'auto_survey_services', data.auto_survey_services);
        this.$set(this.me.settings, 'auto_survey_non_services', data.auto_survey_non_services);
      }
    },
    async saveAiFeedback(item) {
      this.savingAiFeedback = true;
      await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/set-ai-feedback`, {
        lead_id: item.id,
        ai_feedback: item.ai_feedback,
        ai_feedback_urgent: item.ai_feedback_urgent
      }, {
        headers: { 'Authorization': 'Bearer ' + this.$store.state.auth.jwt }
      });
      this.savingAiFeedback = false;
      if (this.ai_messages_lead) this.ai_messages_lead.show_ai_feedback = false;
    },
    filterDate() {
      if (!this.date_filter[1]) return;
      this.show_picker = false;
      this.selected_date_text = '';
      this.getMessages();
    },
    applyDate(selected_date) {
      this.all_time = 0;
      this.selected_date_text = selected_date;
      switch (selected_date) {
        case 'This Week':
          this.date_filter[0] = moment().startOf('week').format('YYYY-MM-DD');
          this.date_filter[1] = moment().endOf('week').format('YYYY-MM-DD');
          break;
        case 'Last Week':
          this.date_filter[0] = moment().startOf('week').subtract(7, 'days').format('YYYY-MM-DD');
          this.date_filter[1] = moment().endOf('week').subtract(7, 'days').format('YYYY-MM-DD');
          break;
        case 'This Month':
          this.date_filter[0] = moment().startOf('month').format('YYYY-MM-DD');
          this.date_filter[1] = moment().endOf('month').format('YYYY-MM-DD');
          break;
        case 'Last Month':
          this.date_filter[0] = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
          this.date_filter[1] = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
          break;
        case 'Last 60 days':
          this.date_filter[0] = moment().subtract(60, 'days').format('YYYY-MM-DD');
          this.date_filter[1] = moment().format('YYYY-MM-DD');
          break;
        case 'Last 90 days':
          this.date_filter[0] = moment().subtract(90, 'days').format('YYYY-MM-DD');
          this.date_filter[1] = moment().format('YYYY-MM-DD');
          break;
        case 'This Year':
          this.date_filter[0] = moment().startOf('year').format('YYYY-MM-DD');
          this.date_filter[1] = moment().endOf('year').format('YYYY-MM-DD');
          break;
        case 'Last Year':
          this.date_filter[0] = moment().startOf('year').subtract(1, 'year').format('YYYY-MM-DD');
          this.date_filter[1] = moment().endOf('year').subtract(1, 'year').format('YYYY-MM-DD');
          break;
        case 'All Time':
          this.date_filter[0] = moment().startOf('year').subtract(10, 'years').format('YYYY-MM-DD');
          this.date_filter[1] = moment().format('YYYY-MM-DD');
          this.all_time = 1;
          break;
        default:
          this.date_filter[0] = moment().subtract(30, 'days').format('YYYY-MM-DD');
          this.date_filter[1] = moment().format('YYYY-MM-DD');
      }
      this.show_picker = false;
      this.getMessages();
    },
    async toggleChecked(item) {
      const prev = item.checked_at;
      item.checked_at = prev ? null : new Date().toISOString();
      try {
        const { data } = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/toggle-checked`, { lead_id: item.id }, {
          headers: { 'Authorization': 'Bearer ' + this.$store.state.auth.jwt }
        });
        item.checked_at = data.checked_at;
      } catch (e) {
        item.checked_at = prev;
      }
    },
    async toggleStar(item) {
      const prev = item.starred_at;
      item.starred_at = prev ? null : new Date().toISOString();
      try {
        const { data } = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/toggle-starred`, { lead_id: item.id }, {
          headers: { 'Authorization': 'Bearer ' + this.$store.state.auth.jwt }
        });
        item.starred_at = data.starred_at;
      } catch (e) {
        item.starred_at = prev;
      }
    },
    leadTypeIcon(item) {
      if (item.lead_type === 'PHONE_CALL') return 'mdi mdi-phone';
      if (item.lead_type === 'MESSAGE') {
        if (item.source && item.source.toLowerCase().includes('web')) return 'mdi mdi-chat';
        return 'mdi mdi-message-text';
      }
      return 'mdi mdi-briefcase';
    },
    openLeadDialog(item, opts = {}) {
        this.activeLead = item;

        console.log('item', item)

        let newTags = [];

        for (var i in this.activeLead.tag) {
            if (this.activeLead.tag[i] != "Complete" && this.activeLead.tag[i] != "Incomplete" && this.activeLead.tag[i] != "Duplicate Call") {
                newTags.push(this.activeLead.tag[i]);
            }
        }

        this.activeLead.tag = newTags;

        if (this.activeLead.photos) {
            this.photos = this.activeLead.photos.split(',')
        } else {
            this.photos = []
        }

        this.leadDialogOpen = true;
        this.leadDialogTab = opts.tab || 'overview';

        // pick a default channel tab
        const providers = this.normalizedProviders(item) || [];
        const firstKey = (providers[0] && providers[0].key) ? providers[0].key : null;
        this.activeLeadChannel = opts.channel || firstKey || (item.sources && item.sources.indexOf('web') !== -1 ? 'web' : null);

        // lock background scroll
        document.documentElement.classList.add('rg-modal-open');
        // document.body.classList.add('rg-modal-open');
    },
    closeLeadDialog() {
        this.leadDialogOpen = false;
        this.activeLead = null;
        this.leadDialogTab = 'overview';
        this.activeLeadChannel = null;

        document.documentElement.classList.remove('rg-modal-open');
        // document.body.classList.remove('rg-modal-open');
    },
    async loadActiveTranscript({ force = false } = {}) {
        if (!this.activeLead || !this.activeLeadChannel) return;
        if (this.leadDialogTab !== 'transcripts') return;

        const lead = this.activeLead;
        const channel = this.activeLeadChannel;

        const cacheKey = `${lead.id}:${channel}`;
        if (!force && this.transcriptCache[cacheKey]) {
            this.transcriptData = JSON.parse(JSON.stringify(this.transcriptCache[cacheKey]));
            return;
        }

        this.transcriptsLoading = true;

        try {
            const headers = { 'Authorization': 'Bearer ' + this.$store.state.auth.jwt };

            // Reset current view data
            const next = { bland: null, lsa_recordings: [], lsa_messages: [], web: null };

            if (lead.sources.indexOf('lsa') != -1) {
                if (lead.lead_type === 'PHONE_CALL') {
                    const resp = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/get-lsa-recordings`, { lead_id: lead.id }, { headers });
                    next.lsa_recordings = (resp && resp.data && Array.isArray(resp.data.recordings)) ? resp.data.recordings : [];

                    const t = (resp && resp.data && Array.isArray(resp.data.transcription)) ? resp.data.transcription : [];

                    next.lsa_transcript = t.map(m => ({
                        sender: (m.speaker == "A") ? 'bot' : 'human',
                        role: (m.speaker == "A") ? 'assistant' : 'user',
                        message: m.text || m.message || m.content || '',
                        timestamp:
                                m.timestamp ||
                                m.event_time ||
                                m.time ||
                                (
                                    this.activeLead.lead_creation_date && m.start != null
                                        ? moment.utc(this.activeLead.lead_creation_date).add((m.start/1000), 'seconds').toISOString()
                                        : null
                                )
                    }));
                } else {
                    const resp = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/get-lsa-messages`, { lead_id: lead.id }, { headers });
                    const msgs = (resp && resp.data && Array.isArray(resp.data.lsa_messages)) ? resp.data.lsa_messages : [];
                    next.lsa_messages = msgs.map(m => ({
                        sender: (m.participant_type == 3) ? 'human' : 'bot',
                        role: (m.participant_type == 3) ? 'user' : 'assistant',
                        message: m.text || m.message || m.content || '',
                        timestamp: m.event_time || m.timestamp || null
                    }));
                }
            }
            else if (lead.sources.indexOf('web') != -1) {
                const resp = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/get-chat-conversation`, { lead_id: lead.id }, { headers });
                const t = resp && resp.data ? resp.data.transcription : null;

                if (t && t.messages && Array.isArray(t.messages)) {
                    next.web = {
                        messages: t.messages.map(m => ({
                            sender: m.sender || ((m.is_person || m.role == 'user') ? 'human' : 'bot'),
                            message: m.message || m.text || m.content || '',
                            timestamp: m.timestamp || m.event_time || m.time || null
                        }))
                    };
                } else {
                    next.web = { messages: [] };
                }
            }
            else if (lead.sources.indexOf('bland') != -1) {
                const resp = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/get-bland-transcript`, { lead_id: lead.id }, { headers });
                const t = resp && resp.data ? resp.data.transcription : null;

                this.activeLead.bland_recording_url = resp.data.recording_url;

                // normalize to {messages:[{sender,message,timestamp}]}
                if (t && t.messages && Array.isArray(t.messages)) {
                    next.bland = {
                        messages: t.messages.map(m => ({
                            sender: m.sender || m.role ||  (m.is_person ? 'human' : 'bot'),
                            message: m.message || m.text || m.content || '',
                            timestamp:
                                m.timestamp ||
                                m.event_time ||
                                m.time ||
                                (
                                    this.activeLead.lead_creation_date && m.start != null
                                        ? moment.utc(this.activeLead.lead_creation_date).add((m.start/1000), 'seconds').toISOString()
                                        : null
                                )
                        }))
                    };
                } else {
                    next.bland = { messages: [] };
                }
            }

            this.transcriptCache[cacheKey] = JSON.parse(JSON.stringify(next));
            this.transcriptData = next;
        } catch (e) {
            console.error('Failed to load transcript:', e);
            // Keep current transcriptData but stop loading
        } finally {
            this.transcriptsLoading = false;
        }
    },
    leadTypeLabel(item) {
      if (!item) return '-';
      if (item.lead_type === 'PHONE_CALL') return 'Call';
      if (item.lead_type === 'MESSAGE') return 'Message';
      return item.lead_type || 'Lead';
    },
    providerLabel(key) {
      const labels = {
        lsa: 'LSA',
        gbp: 'GBP',
        google_ads: 'Google Ads',
        facebook_ads: 'Facebook Ads',
        yelp: 'Yelp',
        website_chat: 'Website Chat',
        website_call: 'Website Call',
        ai: 'AI',
      };
      return labels[key] || (key || '').toUpperCase();
    },
    handlePhotoUpload(event) {
      const files = event.target.files;
      this.uploading = true;
      for (let i = 0; i < files.length; i++) {
        let formData = new FormData();
        formData.append('file', files[i]);
        axios.post(`${process.env.VUE_APP_API_ENDPOINT}/me/upload-image`, formData, {
          headers: { 'Authorization': 'Bearer ' + this.$store.state.auth.jwt }
        }).then(res => {
          this.photos.push(res.data.url);
          this.uploading = false;
        });
      }
    },
    removePhoto(index) { this.photos.splice(index, 1); },
    async switchAiResponseEnabled(lead) {
      await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/set-ai-response-enabled`, {
        lead_id: lead.id, ai_enabled: lead.ai_enabled
      }, {
        headers: { 'Authorization': 'Bearer ' + this.$store.state.auth.jwt }
      });
    },
    // Controls which series are visible using stroke.width and markers.size arrays.
    // For line charts, fill.opacity has NO effect on the line — stroke.width is the only control.
    // hoveredName = null → restore to active/inactive state.
    // hoveredName = 'SeriesName' → show only that series, make everything else invisible.
    _applyVisibility(hoveredName) {
      if (!this.$refs.apexChart) return;
      var series = this.lineChartMetricsSeries;
      var active = this.activeSeriesNames;
      var baseColors = this.seriesColors;

      var strokeWidths = series.map(function(s) {
        if (hoveredName !== null) {
          return s.name === hoveredName ? 4 : 0;
        }
        return active.indexOf(s.name) !== -1 ? 4 : 0;
      });

      // Also update colors: transparent (#00000000) for invisible series so tooltip markers disappear
      var colors = series.map(function(s, idx) {
        var visible = hoveredName !== null
          ? s.name === hoveredName
          : active.indexOf(s.name) !== -1;
        return visible ? (baseColors[idx] || '#000000') : 'transparent';
      });

      this.$refs.apexChart.updateOptions({
        stroke: { curve: 'smooth', width: strokeWidths },
        markers: { size: 0 },
        colors: colors
      }, false, false);
    },

    handleLegendHover(item) {
      if (!this.$refs.apexChart) return;
      this.hoveredLegend = item.name;

      // Apply visual: only hovered series visible, everything else invisible
      this._applyVisibility(item.name);

      // Show Y-axis range for the hovered series
      var hoveredSeries = null;
      for (var i = 0; i < this.lineChartMetricsSeries.length; i++) {
        if (this.lineChartMetricsSeries[i].name === item.name) {
          hoveredSeries = this.lineChartMetricsSeries[i];
          break;
        }
      }
      if (!hoveredSeries) return;

      var minMax = this.getSeriesMinMax(hoveredSeries);
      var min = minMax.min;
      var max = minMax.max;
      var self = this;
      var yaxis = this.lineChartMetricsOptions.yaxis.map(function(axis) {
        var isTarget = axis.seriesName === item.name;
        return Object.assign({}, axis, {
          show: isTarget,
          labels: Object.assign({}, axis.labels || {}, {
            show: isTarget,
            formatter: isTarget ? self.getYAxisFormatter(item.name) : undefined
          }),
          min: isTarget ? min : axis.min,
          max: isTarget ? max : axis.max,
          tickAmount: isTarget ? 1 : undefined
        });
      });
      this.$refs.apexChart.updateOptions({ yaxis: yaxis }, false, false);
    },

    handleLegendLeave() {
      if (!this.$refs.apexChart) return;
      this.hoveredLegend = null;

      // Restore opacity state to active/inactive (no hover)
      this._applyVisibility(null);

      // Hide all Y-axis labels and release their layout space while preserving scale
      var yaxis = this.lineChartMetricsOptions.yaxis.map(function(axis) {
        return Object.assign({}, axis, {
          show: false,
          tickAmount: undefined,
          labels: Object.assign({}, axis.labels || {}, { show: false })
        });
      });
      this.$refs.apexChart.updateOptions({ yaxis: yaxis }, false, false);
    },

    toggleSeries(item) {
      if (!this.$refs.apexChart) return;

      var idx = this.activeSeriesNames.indexOf(item.name);
      if (idx !== -1) {
        // Deactivate: remove from active
        this.activeSeriesNames.splice(idx, 1);
      } else {
        // Activate: add to active
        this.activeSeriesNames.push(item.name);
      }

      // Apply the updated opacity state (no hover active during a click)
      this.hoveredLegend = null;
      this._applyVisibility(null);
    },
    getMonthlyReport() {
      window.open('/leads-public-survey/' + (this.me ? this.me.uuid : '') + "?cutoff_date=" + moment().subtract(2, 'weeks').format('YYYY-MM-DD'));
    },
    async sendMonthlyReport() {
      this.$swal({ title: "Email sent!", text: "The report has been sent!", icon: "success" });
      await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/send-monthly-survey-report`, { now: 2 }, {
        headers: { 'Authorization': 'Bearer ' + this.$store.state.auth.jwt }
      });
    },
    downloadCsv() {
      let csv = 'Customer, Phone, Address, Timestamp, Lead Type, Sources, Charge Status, Satisfaction, Stage\n';
      const leadsToExport = (this.filteredLeads && this.filteredLeads.length > 0) ? this.filteredLeads : this.messages;
      leadsToExport.forEach((row) => {
        const phone = row.ai_provided_phone_number || row.customer_phone_number || row.tracking_number || '';
        const address = (row.customer_address || row.address || '').replace(/"/g, '""');
        csv += `"${(row.customer_name || '').replace(/"/g, '""')}","${phone}","${address}","${this.displayLeadCreationDate(row)}","${row.lead_type || ''}","${row.sources || ''}","${row.charge_status || ''}","${row.satisfaction || ''}","${row.lead_stage || ''}"\n`;
      });
      const anchor = document.createElement('a');
      anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
      anchor.download = 'diyai-leads.csv';
      anchor.click();
    },
    async archiveLead(item) {
      await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/archive-lead`, { id: item.id }, {
        headers: { 'Authorization': 'Bearer ' + this.$store.state.auth.jwt }
      });
      this.getMessages(true);
    },
    async unArchiveLead(item) {
      await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/unarchive-lead`, { id: item.id }, {
        headers: { 'Authorization': 'Bearer ' + this.$store.state.auth.jwt }
      });
      this.getMessages(true);
    },
    async addLeadToKnowledgebase(item) {
      await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/add-lead-to-knowledgebase`, { id: item.id }, {
        headers: { 'Authorization': 'Bearer ' + this.$store.state.auth.jwt }
      });
      this.$swal({ title: "Added!", text: "Lead submitted to Knowledgebase!", icon: "success" });
    },
    async saveNote() {
      if (!this.new_note) return;
      this.savingNote = true;
      const res = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/add-note`, {
        lead_id: this.activeLead.id, note: this.new_note
      }, {
        headers: { 'Authorization': 'Bearer ' + this.$store.state.auth.jwt }
      });
      this.savingNote = false;
      this.activeLead = res.data.lsa_lead;
      this.new_note = '';
      this.getMessages(true);
    },
    async deleteNote(index) {
      const res = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/delete-note`, {
        lead_id: this.activeLead.id, note_index: index
      }, {
        headers: { 'Authorization': 'Bearer ' + this.$store.state.auth.jwt }
      });
      this.activeLead = res.data.lsa_lead;
      this.getMessages(true);
    },
    showEditCustomerName(item) {
      $(`.lsa-show-customer-name-${item.id}`).hide();
      $(`.lsa-edit-customer-name-${item.id}`).show();
    },
    cancelEditCustomerName(item) {
      $(`.lsa-show-customer-name-${item.id}`).show();
      $(`.lsa-edit-customer-name-${item.id}`).hide();
    },
    async editCustomerName(item) {
      item.customer_name = $(`.lsa-edit-customer-name-${item.id} input`).val();
      const res = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/edit-customer-name`, { item }, {
        headers: { 'Authorization': 'Bearer ' + this.$store.state.auth.jwt }
      });
      this.activeLead = res.data.lead;
      this.cancelEditCustomerName(item);
      this.getMessages(true);
    },
    displaySpeakerTimestamp(milliseconds) {
      var hours = Math.floor(milliseconds / (1000 * 60 * 60));
      var minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
      var seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);
      return `${hours}:${minutes}:${seconds}`;
    },
    readCutoffFromQuery() {
      const params = new URLSearchParams(window.location.search);
      const cutoff = params.get("cutoff_date");
      this.cutoffDate = cutoff ? moment(cutoff) : moment().subtract(2, 'weeks');
    },
    isNewLead(item) {
      return item.lead_creation_date && moment(item.lead_creation_date).isAfter(this.cutoffDate);
    },
    getRowClass(item) {
      return this.isNewLead(item) ? "new-lead-highlight" : "";
    },
    getHost() { return window.location.host; },
    isOnlyAi() { return !this.isAccelerate(); },
    isAccelerate() { return this.getHost() == 'lsa.accelerateyourmarketing.com'; },
    formatDate(date, format = "MM/DD/YYYY") { return moment(String(date)).format(format); },
    displayLeadCreationDate(lead) {
      if (!String(lead.sources || '').split(',').some(source => source.trim() === 'lsa')) {
        return this.displayTimestamp(lead, lead.lead_creation_date);
      }
      return formatLeadCreationDate(lead, this.me && this.me.timezone);
    },
    displayTimestamp(item, date) {
      if (!date) return '';

      let userTimezone = ((this.me.timezone) ? this.me.timezone : 'America/New_York');

      if (item.sources !== 'bland') {
        // Already in local Chicago time from LSA API
        return moment.tz(date, userTimezone).format('ddd M/D/YYYY h:mm:ss A');
      }

      // Local DB entry (stored in UTC) -> convert to target timezone
      return moment.utc(date).tz(userTimezone).format('ddd M/D/YYYY h:mm:ss A');
    },
    trainAgentWithSelectedCalls() {
      this.selected = [];
      this.$swal({ title: "Saved", text: "Selected calls will train the AI Agent!" });
    },
    satisfactionLabelByTexts(value) {
      const map = { "VERY_SATISFIED": 'Very Satisfied', "SATISFIED": 'Satisfied', "NEUTRAL": 'Neutral', "DISSATISFIED": 'Unsatisfied', "VERY_DISSATISFIED": 'Very Unsatisfied' };
      return map[value] || 'Not Set';
    },
    convertSatisfactionToNumber(value) {
      const map = { "VERY_SATISFIED": 5, "SATISFIED": 4, "NEUTRAL": 3, "DISSATISFIED": 2, "VERY_DISSATISFIED": 1 };
      return map[value] || 3;
    },
    getSatisfactionColor(value) {
      const map = { "1": "#ef4444", "2": "#fb923c", "3": "#facc15", "4": "#22c55e", "5": "#16a34a" };
      return map[value] || '#94a3b8';
    },
    normalizedProviders(item) {
      const src = (item.sources || '').toLowerCase();
      const leadType = (item.lead_type || '').toUpperCase();
      const list = [];

      // Source icon — one per lead based on where it came from
      if (src.includes('lsa')) {
        list.push({ key: 'lsa', title: 'Google Local Service Ads lead', src: require('@/assets/images/lsa.webp') });
      } else if (src.split(',').some(source => ['google_ads', 'facebook_ads'].includes(source.trim()))) {
        const source = src.split(',').map(value => value.trim()).find(value => ['google_ads', 'facebook_ads'].includes(value));
        const provider = phoneSource(source);
        list.push({ key: source, title: `${provider.label} lead`, src: provider.icon });
      } else if (src.includes('gbp')) {
        list.push({ key: 'gbp', title: 'Google Business Profile lead', src: require('@/assets/images/reviewplatforms/icons/icon-googlemaps.png') });
      } else if (src.includes('yelp')) {
        list.push({ key: 'yelp', title: 'Yelp lead', src: require('@/assets/images/reviewplatforms/icons/icon-yelp.png') });
      } else if (src.includes('web')) {
        if (leadType === 'MESSAGE') {
          list.push({ key: 'website_chat', title: 'Website Chat Widget lead', src: 'https://cdn-icons-png.flaticon.com/512/1006/1006771.png' });
        } else {
          list.push({ key: 'website_call', title: 'Website Call lead', src: 'https://cdn-icons-png.flaticon.com/512/1006/1006771.png' });
        }
        // Web leads are always handled by AI
        list.push({ key: 'ai', title: 'This lead was responded to by AI', src: require('@/assets/images/bland.png') });
      }

      // AI handling icon — shown once when bland AI responded (non-web leads)
      if (src.includes('bland') && !src.includes('web')) {
        list.push({ key: 'ai', title: 'This lead was responded to by AI', src: require('@/assets/images/bland.png') });
      }

      return list;
    },
    async getMessages(silent = false) {
      if (!silent) { this.loadingMessages = true; this.loading = true; }
      try {
        const response = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/get-messages`, {
          date_filter: this.date_filter,
          filter_only_lsa: this.filter_calls_type === 'LSA Leads' ? 1 : 0,
          filter_calls_type: this.filter_calls_type,
          lead_quality_filter: this.lead_quality_filter,
          performance_filter: this.performance_filter,
          source_filter: this.source_filter
        }, {
          headers: { 'RG-Auth-Loc-Uu': this.getUUID() }
        });

        this.messages = response.data.messages || [];
        this.company_name = response.data.company_name || '';
        this.ai_started_on = response.data.ai_started_on || '';
        this.survey_started_on = response.data.survey_started_on || '';
        this.first_lead_on = response.data.first_lead_on || '';

        // Automatically check URL hash and open lead dialog if leadId is present
        this.$nextTick(() => {
          this.checkUrlHashForLead();
        });
        
        // FETCH BOTH DASHBOARD & HISTORICAL CHART DATA
        await this.loadDashboard();
        await this.loadDashboard2();
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingMessages = false;
        this.loading = false;
      }
    },

    /* RESTORED dashboard_data_prev FETCHING LOGIC */
    async loadDashboard() {
      const response = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/me/get-dashboard-data-diy`, {
        start_date: this.date_filter[0],
        end_date: this.date_filter[1],
        all_time: 0,
        selected_frequency: "Daily",
        tags_selected: '[]',
        public_survey: 1,
        filter_only_lsa: this.filter_calls_type === 'LSA Leads' ? 1 : 0,
        filter_calls_type: this.filter_calls_type,
        lead_quality_filter: this.lead_quality_filter,
        performance_filter: this.performance_filter,
        source_filter: this.source_filter
      }, {
        headers: { 'RG-Auth-Loc-Uu': this.getUUID() }
      });

      this.dashboard_data = response.data.dashboard_data || {};
      const impactStats = this.dashboard_data.impact_stats || {};
      this.stats = {
        websiteChats: impactStats.websiteChats || 0,
        lsaCalls: impactStats.lsaCalls || 0,
        lsaMessages: impactStats.lsaMessages || 0,
        followUpsSent: impactStats.followUpsSent || 0,
        surveysSubmitted: impactStats.surveysSubmitted || 0,
        spamCallsBlocked: impactStats.spamCallsBlocked || 0,
        hoursSaved: impactStats.hoursSaved || 31
      };

      // Compute date range for previous period
      let diff_days = moment(this.date_filter[1]).diff(this.date_filter[0], 'days') + 1;
      let date_prev_start = moment(this.date_filter[0]).subtract(diff_days, 'days').format('YYYY-MM-DD');
      let date_prev_end = moment(this.date_filter[1]).subtract(diff_days, 'days').format('YYYY-MM-DD');

      if (diff_days >= 28 && diff_days <= 31 && this.selected_frequency != 'Frequency: Auto') {
        date_prev_start = moment(this.date_filter[0]).subtract(1, 'months').format('YYYY-MM-DD');
        date_prev_end = moment(date_prev_start).endOf('month').format('YYYY-MM-DD');
      }

      if (diff_days >= 28 && diff_days <= 31 && this.selected_frequency == 'Frequency: Auto') {
        date_prev_start = moment(this.date_filter[0]).subtract(1, 'months').format('YYYY-MM-DD');
        date_prev_end = moment(date_prev_start).add(1, 'months').format('YYYY-MM-DD');
      }

      // Fetch previous period dashboard data into dashboard_data_prev
      try {
        const response2 = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/me/get-dashboard-data-diy`, {
          start_date: date_prev_start,
          end_date: date_prev_end,
          all_time: 0,
          selected_frequency: "Daily",
          tags_selected: '[]',
          public_survey: 1,
          filter_only_lsa: this.filter_calls_type === 'LSA Leads' ? 1 : 0,
          filter_calls_type: this.filter_calls_type,
          lead_quality_filter: this.lead_quality_filter,
          performance_filter: this.performance_filter,
          source_filter: this.source_filter
        }, {
          headers: { 'RG-Auth-Loc-Uu': this.getUUID() }
        });

        this.dashboard_data_prev = response2.data.dashboard_data || {};
      } catch (errPrev) {
        console.error("Error fetching previous period data:", errPrev);
        this.dashboard_data_prev = {};
      }

      this.calculateComparisons();
    },

    async loadDashboard2() {
      try {
        const chartStartDate = (this.first_lead_on && moment(this.first_lead_on).isValid())
          ? moment(this.first_lead_on).format('YYYY-MM-DD')
          : moment().subtract(6, 'months').startOf('month').format('YYYY-MM-DD');

        let endDate = moment().endOf('month').format('YYYY-MM-DD');

        // if (this.selected_frequency_chart == 'Monthly') {
        //   endDate = moment().add(1, 'month').endOf('month').format('YYYY-MM-DD');
        // }

        let response = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/me/get-dashboard-data-diy`, {
            start_date: chartStartDate,
            end_date: endDate,
            all_time: 0,
            selected_frequency: this.selected_frequency_chart,
            tags_selected: '[]',
            public_survey: 1,
            filter_only_lsa: this.filter_calls_type === 'LSA Leads' ? 1 : 0,
            filter_calls_type: this.filter_calls_type,
            source_filter: this.source_filter,
            lead_quality_filter: this.lead_quality_filter,
            performance_filter: this.performance_filter
          },
          {
            headers: { 'RG-Auth-Loc-Uu': this.getUUID() }
          }
        );


        // if (this.ai_started_on) {
        //     for (var i in response.data.dashboard_data.metrics_chart.labels) {
        //         if (moment(response.data.dashboard_data.metrics_chart.labels[i]) >= moment(this.ai_started_on)) {
        //             response.data.dashboard_data.metrics_chart.labels[i] = response.data.dashboard_data.metrics_chart.labels[i];
        //         }
        //     }
        // }

        // Recreate with a coherent series/axis set. Updating axis count on the
        // old chart first makes ApexCharts read missing series during filtering.
        this.metricsChartReady = false;
        await this.$nextTick();
        const newCategories = response.data.dashboard_data.metrics_chart.labels;

        // console.log('newCategories', newCategories)

        this.lineChartMetricsOptions.xaxis.categories = newCategories

        this.lineChartMetricsOptions.annotations = {
            xaxis: [
              {
                x: moment(this.ai_started_on).format('MMM YYYY'),
                borderColor: "#008FFB",
                strokeDashArray: 4,
                label: {
                  borderColor: "#008FFB",
                  style: {
                    color: "#fff",
                    background: "#008FFB"
                  },
                  text: "AI Started " + moment(this.ai_started_on).format('MM/DD')
                }
              },
              // {
              //   x: moment(this.survey_started_on).format('MMM YYYY'),
              //   borderColor: "#FF4560",
              //   strokeDashArray: 4,
              //   label: {
              //     borderColor: "#FF4560",
              //     style: {
              //       color: "#fff",
              //       background: "#FF4560"
              //     },
              //     text: "Survey Started " + moment(this.survey_started_on).format('MM/DD')
              //   }
              // }
            ]
          }


        // First 3 series are active by default; the rest are hidden until clicked
        this.activeSeriesNames = ['Under 30s Response Rate', 'Lead Volume', 'Lead Quality'];

        // Build full series list — LSA-only series are excluded when a non-LSA source is selected
        var lsaOnly = this.lsaOnlySeriesNames;
        var showLsa = this.isLsaSource;
        var allSeries = [
          {
            name: 'Under 30s Response Rate',
            data: response.data.dashboard_data.metrics_chart.datasets['Under 30s Response Rate']
          },
          {
            name: 'Lead Volume',
            data: response.data.dashboard_data.metrics_chart.datasets['Lead Volume']
          },
          {
            name: 'Lead Quality',
            data: response.data.dashboard_data.metrics_chart.datasets['Lead Quality']
          },
          {
            name: 'Impressions',
            data: response.data.dashboard_data.metrics_chart.datasets['Impressions']
          },
          {
            name: 'Absolute Top Impressions Rate',
            data: response.data.dashboard_data.metrics_chart.datasets['Absolute Top Impressions']
          },
          {
            name: 'Reviews',
            data: response.data.dashboard_data.metrics_chart.datasets['Reviews']
          },
          {
            name: 'Clicks',
            data: response.data.dashboard_data.metrics_chart.datasets['Clicks']
          },
          {
            name: 'Total Cost',
            data: response.data.dashboard_data.metrics_chart.datasets['Cost']
          },
          {
            name: 'Credited',
            data: response.data.dashboard_data.metrics_chart.datasets['Credited']
          },
          {
            name: 'CPL',
            data: response.data.dashboard_data.metrics_chart.datasets['CPL']
          }
        ];

        const conversionDatasets = response.data.dashboard_data.metrics_chart.datasets;
        ['Conversions', 'Conversion Rate', 'Collected Revenue', 'Ad-spend ROI'].forEach(name => {
          allSeries.push({ name, data: conversionDatasets[name] || [] });
        });

        // Filter out LSA-only series for non-LSA source selections
        this.lineChartMetricsSeries = showLsa
          ? allSeries
          : allSeries.filter(function(s) { return lsaOnly.indexOf(s.name) === -1; });

        // CRITICAL: yaxis must have exactly one entry per series (matched by seriesName).
        // Always filter from the FULL static yaxis config (allYaxisConfig), not the
        // already-mutated yaxis — so switching back to LSA source restores all entries.
        var activeSeriesNameSet = this.lineChartMetricsSeries.map(function(s) { return s.name; });
        var allYaxisConfig = [
          { seriesName: 'Under 30s Response Rate', show: false, opposite: false, min: 0, max: 100, labels: { show: false } },
          { seriesName: 'Lead Volume',              show: false, opposite: false, min: 0,           labels: { show: false } },
          { seriesName: 'Lead Quality',             show: false, opposite: false, min: 0, max: 5,   labels: { show: false } },
          { seriesName: 'Impressions',              show: false, opposite: false, min: 0,           labels: { show: false } },
          { seriesName: 'Absolute Top Impressions Rate', show: false, opposite: false, min: 0, max: 100, labels: { show: false } },
          { seriesName: 'Reviews',                  show: false, opposite: false, min: 0,           labels: { show: false } },
          { seriesName: 'Clicks',                   show: false, opposite: false, min: 0,           labels: { show: false } },
          { seriesName: 'Total Cost',               show: false, opposite: false, min: 0,           labels: { show: false } },
          { seriesName: 'Credited',                 show: false, opposite: false, min: 0,           labels: { show: false } },
          { seriesName: 'CPL',                      show: false, opposite: false, min: 0,           labels: { show: false } }
        ];
        allYaxisConfig.push(
          { seriesName: 'Conversions', show: false, min: 0, labels: { show: false } },
          { seriesName: 'Conversion Rate', show: false, min: 0, max: 100, labels: { show: false } },
          { seriesName: 'Collected Revenue', show: false, min: 0, labels: { show: false } },
          { seriesName: 'Ad-spend ROI', show: false, labels: { show: false } }
        );
        this.lineChartMetricsOptions.yaxis = allYaxisConfig.filter(function(axis) {
          return activeSeriesNameSet.indexOf(axis.seriesName) !== -1;
        });

        // Pre-set colors and stroke BEFORE assigning series so ApexCharts renders
        // with correct initial visibility in one pass (no flash of all series).
        var initialActive = this.activeSeriesNames;
        this.lineChartMetricsOptions.colors = this.lineChartMetricsSeries.map(function(s, i) {
          return initialActive.indexOf(s.name) !== -1 ? (this.seriesColors[i] || '#000') : 'transparent';
        }, this);
        this.lineChartMetricsOptions.stroke = {
          curve: 'smooth',
          width: this.lineChartMetricsSeries.map(function(s) {
            return initialActive.indexOf(s.name) !== -1 ? 4 : 0;
          })
        };

        this.metricsChartReady = true;

      } catch (e) {
        console.error("Error loading historical chart metrics:", e);
      }
    },

    calculateComparison(now, prev) {
  now = parseFloat(now || 0);
  prev = parseFloat(prev || 0);

  if (now === 0 && prev === 0) return 0;
  if (prev === 0) return 100;

  // Calculates true signed percentage change (positive for growth, negative for drop)
  let percentage = ((now - prev) / prev) * 100;
  return parseFloat(percentage.toFixed(2));
},

    calculateComparisons() {
      if (!this.dashboard_data_prev) return;
      this.comparisons.total_leads = this.calculateComparison(this.dashboard_data.total_leads, this.dashboard_data_prev.total_leads);
      this.comparisons.total_calls = this.calculateComparison(this.dashboard_data.total_calls, this.dashboard_data_prev.total_calls);
      this.comparisons.total_message_leads = this.calculateComparison(this.dashboard_data.total_message_leads, this.dashboard_data_prev.total_message_leads);
      this.comparisons.charged_leads_total = this.calculateComparison(this.dashboard_data.charged_leads_total, this.dashboard_data_prev.charged_leads_total);
      this.comparisons.lead_quality = this.calculateComparison(this.dashboard_data.lead_quality, this.dashboard_data_prev.lead_quality);
      if (this.dashboard_data.stats && this.dashboard_data_prev.stats) {
        this.comparisons.lsa_30_seconds_response_rate = this.calculateComparison(
          this.dashboard_data.stats.lsa_30_seconds_response_rate,
          this.dashboard_data_prev.stats.lsa_30_seconds_response_rate
        );
        this.comparisons.total_impressions = this.calculateComparison(
          this.dashboard_data.stats.total_impressions,
          this.dashboard_data_prev.stats.total_impressions
        );
        this.comparisons.top_impression_rate = this.calculateComparison(
          this.dashboard_data.stats.top_impression_rate,
          this.dashboard_data_prev.stats.top_impression_rate
        );
        this.comparisons.absolute_top_impression_rate = this.calculateComparison(
          this.dashboard_data.stats.absolute_top_impression_rate,
          this.dashboard_data_prev.stats.absolute_top_impression_rate
        );
      }
      this.comparisons.review_requests_sent = this.calculateComparison(
        this.dashboard_data.review_requests_sent,
        this.dashboard_data_prev.review_requests_sent
      );
      this.comparisons.review_links_clicked = this.calculateComparison(
        this.dashboard_data.review_links_clicked,
        this.dashboard_data_prev.review_links_clicked
      );
      this.comparisons.review_gathered_count = this.calculateComparison(
        this.dashboard_data.review_gathered_count,
        this.dashboard_data_prev.review_gathered_count
      );
    },
    getUUID() { return this.me ? this.me.uuid : this.$route.params.uuid; }
  },
  created() {
    this.readCutoffFromQuery();
    this.loadCustomTags();
    this.fetchHealth();
    this.getMessages();
  },
  mounted() {
    // nothing needed here
  },

  watch: {
      leadDialogTab(newVal) {
        if (newVal === 'transcripts') {
          this.$nextTick(() => this.loadActiveTranscript({ force: true }));
        }
      },
      activeLeadChannel() {
        if (this.leadDialogTab === 'transcripts') {
          this.loadActiveTranscript();
        }
      },
      activeLead() {
        if (this.leadDialogTab === 'transcripts') {
          this.$nextTick(() => this.loadActiveTranscript({ force: true }));
        }
      }
    }
}
</script>

<style scoped>
.converted-total { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 14px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280; }
.converted-total strong { font-size: 18px; color: #1d4ed8; }

/* ================================================================= */
/* MOCKUP PROPOSAL EXACT DESIGN SYSTEM                               */
/* ================================================================= */

.lsa-modern-dashboard {
  font-family: -apple-system, "SF Pro Rounded", "Segoe UI", Nunito, sans-serif;
  /*background: #f2f5f8;*/
  color: #16283c;
  min-height: 100vh;
}

.sub {
  color: #5b6b7c;
  font-size: 13.5px;
  padding-left :0px !important;
}

.grid { display: grid; gap: 14px; }
.hero { grid-template-columns: 1.2fr 1.05fr .85fr 1.15fr; }
.cards4 { grid-template-columns: repeat(4, 1fr); }
.cards3 { grid-template-columns: repeat(3, 1fr); }

.card {
  background: #fff;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 1px 3px rgba(22,40,60,.07);
  position: relative;
  border: 0px !important;
}

.filters-panel-card {
  background: #ffffff !important;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 4px rgba(22, 40, 60, 0.08);
  border: 1px solid #e2e8f0 !important;
}

.filters-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.filters-panel-card .filter-item {
  display: inline-flex;
  align-items: center;
  margin-right: 12px;
}

.filters-panel-card .filter-item:last-child {
  margin-right: 0;
}

.daterange-picker-btn {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #334155;
  transition: all 0.15s ease-in-out;
}

.daterange-picker-btn:hover {
  border-color: #2fa7b8;
  background-color: #f8fafc;
}

.download-csv-btn {
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  color: #1e293b;
  transition: all 0.15s ease-in-out;
}

.download-csv-btn:hover {
  border-color: #2fa7b8;
  background-color: #f8fafc;
  color: #0f172a;
}

.v-application a {
    color: #276472 !important;
}

.v-application a.text-white {
    color: #FFFFFF !important;
}

.k { font-size: 12px; color: #5b6b7c; font-weight: 700; }
.v { font-size: 28px; font-weight: 800; margin: 4px 0 6px; }
.v small { font-size: 14px; color: #5b6b7c; font-weight: 700; }

/* ================================================================= */
/* 5-LEVEL SCORE BADGE & LEGEND STYLING                              */
/* ================================================================= */

/* Legend Circle Badges */
.legend-badge {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-weight: 800;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
}

/* Base Badge Style */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  white-space: nowrap;
  width: fit-content;
  align-self: flex-start;
}

/* Level 5: Very Good (#16a34a) */
.legend-badge.score-1 {
  background: #15803d;
}
.badge.score-1 {
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

/* Level 4: Good (#22c55e) */
.legend-badge.score-2 {
  background: #166534;
}
.badge.score-2 {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
}

/* Level 3: Neutral (#facc15) */
.legend-badge.score-3 {
  background: #854d0e;
}
.badge.score-3 {
  background: #fefce8;
  color: #854d0e;
  border: 1px solid #fef08a;
}

/* Level 2: Bad (#fb923c) */
.legend-badge.score-4 {
  background: #c2410c;
}
.badge.score-4 {
  background: #fff7ed;
  color: #c2410c;
  border: 1px solid #ffedd5;
}

/* Level 1: Very Bad (#ef4444) */
.legend-badge.score-5 {
  background: #b91c1c;
}
.badge.score-5 {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

/* Default Utility Badges from Proposal */
.badge.down { background: #fdecec; color: #c0392b; }
.badge.up   { background: #e8f6ee; color: #1d7a46; }
.badge.flat { background: #fff3cd; color: #8a6d08; }
.v-application .info { background: #e2eef2 !important; color: #276472 !important; }
.badge.warn { background: #fff6df; color: #9a7008; }

/* CHARGE / DISPUTE BADGE COLOR DISTINCTIONS */
.badge.charge-credited {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.badge.charge-charged {
  background: #dbeafe;
  color: #1e40af;
  border: 1px solid #bfdbfe;
}

.badge.charge-not-charged {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

/* TOOLTIP STYLING PRESERVATION */
.intake-tooltip {
  max-width: 320px;
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.4;
  font-size: 12px;
}

.baseline { font-size: 12px; color: #8593a3; margin-top: 6px; line-height: 1.4; }
h2.rowlabel { font-size: 12px; letter-spacing: .12em; text-transform: uppercase; color: #7a8a9b; margin: 24px 0 10px; font-weight: 800; }

/* HERO RING */
.ring {
  width: 78px;
  height: 78px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}
.ring .inner {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.ring .inner b { font-size: 16px; line-height: 1; }
.ring .inner b small { font-size: 10px; color: #8593a3; }
.ring .inner span { font-size: 9px; font-weight: 800; letter-spacing: .06em; }

.why { font-size: 12px; color: #5b6b7c; margin-top: 4px; line-height: 1.4; }
.bands { display: flex; gap: 8px; margin-top: 8px; font-size: 10.5px; color: #8593a3; }
.dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 3px; }
.money { font-size: 24px; font-weight: 800; color: #1d7a46; }

/* ACTION & TASK CARDS */
.card.action { background: #fff8e8; border: 1.5px solid #f0d48a; }
.card.action .k { color: #9a7008; }
.cta {
  display: inline-block;
  background: #16283c;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 8px;
}
.cta.teal { background: #2fa7b8; }
.link { color: #276472; font-size: 12px; font-weight: 700; text-decoration: none; border-bottom: 1.5px solid #9fdbe4; }

.task { display: flex; gap: 12px; align-items: flex-start; }
.task .tag { font-size: 10px; font-weight: 800; letter-spacing: .1em; padding: 3px 6px; border-radius: 6px; }
.tag.urgent { background: #fdecec; color: #c0392b; }
.tag.disputes { background: #e2eef2; color: #276472; }
.tag.reviews { background: #eef0fb; color: #4a44a8; }
.task b { font-size: 14px; display: block; margin-bottom: 2px; }
.task p { font-size: 12px; color: #5b6b7c; line-height: 1.4; }

/* RECENT LEADS LIST (MOCKUP MATCHING DESIGN) */
.rg-lead-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ================================================================= */
/* BULLETPROOF LEAD ROW LAYOUT                                       */
/* ================================================================= */

.clearfix::after {
  content: "";
  clear: both;
  display: table;
}

.card.lead-row {
  background: #ffffff;
  border-radius: 14px;
  padding: 12px 18px;
  box-shadow: 0 1px 3px rgba(22, 40, 60, 0.07);
  border: 1px solid #e2e8f0;
  margin-bottom: 10px;
  position: relative;
  display: block; /* Pure standard block box model */
  width: 100%;
}

.card.lead-row:hover {
  box-shadow: 0 4px 12px rgba(22, 40, 60, 0.10);
}

/* LEFT CONTENT CONTAINER */
.lead-left {
  float: left;
  text-align: left;
  max-width: 55%;
}

.lead-title-line {
  font-size: 14px;
  line-height: 1.3;
  color: #16283c;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.lead-name {
  font-weight: 800;
  color: #16283c;
}

.lead-type-icon {
  font-size: 16px;
  color: #64748b;
}

.provider-icons {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
}

.channel-icon {
  width: 18px;
  height: 18px;
  object-fit: contain;
  vertical-align: middle;
}

.human-icon {
  font-size: 18px;
  color: #94a3b8;
  vertical-align: middle;
}

.lead-sub-line {
  font-size: 12px;
  color: #8593a3;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

/* RIGHT CONTENT CONTAINER */
.lead-right {
  float: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 2px;
}

/* BADGES STYLING */
.badge {
  display: inline-block;
  font-size: 11.5px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  white-space: nowrap;
}

.badge.info { background: #e2eef2; color: #276472; }

/* 5 SCORE LEVEL BADGES */
.badge.score-1 { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }
.badge.score-2 { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
.badge.score-3 { background: #fefce8; color: #854d0e; border: 1px solid #fef08a; }
.badge.score-4 { background: #fff7ed; color: #c2410c; border: 1px solid #ffedd5; }
.badge.score-5 { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }

/* ACTION BUTTONS TOOLBAR */
.action-buttons {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  border-left: 1px solid #e2e8f0;
  padding-left: 8px;
}

.icon-link {
  border: none;
  background: transparent;
  padding: 4px;
  border-radius: 6px;
  color: #5b6b7c;
  font-size: 18px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}

.icon-link:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.dots-btn {
  color: #64748b !important;
  cursor: pointer;
  margin-left: 4px;
}

.note-counter {
  position: absolute;
  top: -2px;
  right: -2px;
  background: #f97316;
  color: #ffffff;
  border-radius: 999px;
  width: 13px;
  height: 13px;
  font-size: 8px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* RESPONSIVE FALLBACK */
@media (max-width: 900px) {
  .lead-left, .lead-right {
    float: none;
    max-width: 100%;
    width: 100%;
  }
  .lead-right {
    margin-top: 10px;
    justify-content: flex-start;
  }
}

/* PROPOSAL EXACT BADGE DESIGN */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
  width: fit-content;
}

.badge.down { background: #fdecec; color: #c0392b; }
.badge.up { background: #e8f6ee; color: #1d7a46; }
.badge.flat { background: #fff3cd; color: #8a6d08; }
.badge.info { background: #e2eef2; color: #276472; }
.badge.warn { background: #fff6df; color: #9a7008; }

/* ACTION CONTROL LINK BUTTONS */
.icon-link {
  border: 0;
  background: transparent;
  padding: 4px 6px;
  border-radius: 6px;
  color: #5b6b7c;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.icon-link:hover {
  background: #f2f5f8;
  color: #16283c;
}

.note-counter {
  position: absolute;
  top: -2px;
  right: -2px;
  background: #f97316;
  color: #fff;
  border-radius: 999px;
  width: 14px;
  height: 14px;
  font-size: 9px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* CHECKED & HIGHLIGHT STATES */
.lead--checked {
  opacity: 0.5;
  filter: grayscale(0.5);
}

.new-lead-highlight {
  background-color: #fff7cc !important;
  border-left: 4px solid #f5c518 !important;
}

@media (max-width: 900px) {
  .card.lead-row {
    flex-direction: column;
    align-items: flex-start;
  }
  .chips {
    margin-left: 0;
    width: 100%;
    justify-content: flex-start;
  }
}
.lead--checked { opacity: 0.4; filter: grayscale(0.5); }
.new-lead-highlight { background-color: #fff7cc !important; border-left: 4px solid #f5c518 !important; }

.chip.ctx { background: #e2eef2; color: #276472; font-size: 12px; font-weight: 700; padding: 4px 10px; border-radius: 999px; }
.mysterychip { border: 1.5px solid #6c63c8; color: #6c63c8; font-weight: 800; border-radius: 999px; padding: 2px 10px; font-size: 12px; }
.daterange { background: #63c6a8; color: #fff; font-weight: 700; font-size: 12.5px; padding: 8px 14px; border-radius: 10px; }

.rg-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  background: #f3f4f6;
  color: #374151;
}
.rg-pill--warn { background: rgba(245, 197, 24, 0.25); color: #92400e; }
.rg-pill--soft { background: #eef2f6; color: #374151; }

.rg-link { border: 0; background: transparent; padding: 4px; border-radius: 6px; }
.rg-link:hover { background: #f3f4f6; }

/* MODALS FIXED CSS PROPERTIES */
.rg-modal-overlay {
  position: fixed; inset: 0; z-index: 60; background: rgba(0,0,0,0.35);
  display: flex; align-items: center; justify-content: center; padding: 16px;
}
.rg-modal {
  width: min(850px, 100%); max-height: calc(100% - 32px); background: #fff;
  border-radius: 16px; overflow: hidden; box-shadow: 0 30px 70px rgba(0,0,0,0.25);
  display: flex; flex-direction: column;
}
.rg-modal__header { padding: 14px 16px; display: flex; flex-shrink: 0; justify-content: space-between; border-bottom: 1px solid #e5e7eb; }
.rg-modal__tabs { padding: 8px 10px; display: flex; flex-shrink: 0; gap: 6px; overflow-x: auto; border-bottom: 1px solid #e5e7eb; background: #fafafa; }
.rg-tab { padding: 6px 12px; flex-shrink: 0; border-radius: 8px; font-size: 12px; font-weight: 700; color: #374151; }
.rg-tab.active { background: #fff; border: 1px solid #e5e7eb; color: #111827; }
.rg-modal__body { padding: 16px; overflow: auto; }
.rg-modal__footer { padding: 12px 16px; border-top: 1px solid #e5e7eb; display: flex; justify-content: flex-end; }
.rg-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.rg-kv { border: 1px solid #e5e7eb; border-radius: 10px; padding: 10px; }
.rg-k { font-size: 11px; color: #6b7280; font-weight: 700; }
.rg-v { margin-top: 2px; color: #111827; font-size: 13px; }

/* SCORE BADGES */
.legend-items { display: flex; gap: 6px; flex-wrap: wrap; }
.legend-item { display: flex; align-items: center; gap: 4px; font-size: 11px; color: #374151; }
.legend-badge { width: 18px; height: 18px; border-radius: 50%; font-weight: 700; font-size: 10px; display: flex; align-items: center; justify-content: center; color: #fff; }

.custom-legend.centered { display: flex; justify-content: center; flex-wrap: wrap; gap: 16px; }
.legend-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }

@media (max-width: 900px) {
  .hero, .cards4, .cards3 { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 600px) {
  .hero, .cards4, .cards3 { grid-template-columns: 1fr; }
}

h2.rowlabel {
  font-size: 13px;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: #7a8a9b;
  margin: 26px 0 10px;
  font-weight: 800;
}

.card {
  background: #fff;
  border-radius: 14px;
  padding: 18px 22px;
  box-shadow: 0 1px 3px rgba(22, 40, 60, .07);
  position: relative;
}

.trio {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
}

.mini h4 {
  font-size: 12.5px;
  color: #5b6b7c;
  margin-bottom: 6px;
  font-weight: 700;
}

.mini .now {
  font-size: 18px;
  font-weight: 800;
  margin-bottom: 4px;
  color: #16283c;
}

.chartnote {
  font-size: 12px;
  color: #8593a3;
  margin-top: 12px;
  line-height: 1.4;
}

@media (max-width: 900px) {
  .trio {
    grid-template-columns: 1fr;
  }
}

/* Legend hover/active state is fully managed via fill.opacity and stroke.width updateOptions */
</style>
